import { create } from 'zustand';
import { debounce } from 'lodash';
import { isEqual } from 'lodash';

import { spaceCRDTService } from '../services/crdt/spaceCRDTService';
import { spaceService } from '../services/spaceService';
import { useSpatialUIStore } from './spaceSpatialUIStore';
import { AnyWindowEntity, WindowEntity } from '../types/windows';

import { createFileLogger, formatJSON } from '../utils/logger';
import { modelService } from 'services/modelService';
import { SpaceSettings, SpaceBackground, UISpace as Space } from '../types/spaces';
import { DEFAULT_NEW_SPACE_BACKGROUND } from '../constants/spaceBackgrounds';
import { entityService } from '../services/entityService';
import useEntityStore from './entityStore';
import { createSpaceActions } from './spaceActions';
import { getAppTypeForEntity } from '../utils/entityTypeMapping';
import { getWindowEntityId } from '../components/Space/utils/windowUtils';
// Add this type declaration for the global lock
declare global {
  interface Window {
    __SPACE_INITIALIZING__?: boolean;
  }
}

// Add a timestamp marker to track code loading
const log = createFileLogger('SPACE_STORE', { enabled: false });
log.info(`Code loaded with direct persistence at ${new Date().toISOString()}`);

// Unified backend save queue to ensure consistent ordering
const backendSaveQueue = {
  pending: false,
  timer: null as NodeJS.Timeout | null,
  
  schedule: (delayMs: number = 500) => {
    if (backendSaveQueue.timer) {
      clearTimeout(backendSaveQueue.timer);
    }
    
    backendSaveQueue.timer = setTimeout(() => {
      backendSaveQueue.execute();
    }, delayMs);
  },
  
  execute: async () => {
    if (backendSaveQueue.pending) return;
    
    backendSaveQueue.pending = true;
    backendSaveQueue.timer = null;
    
    try {
      const state = useSpaceStore.getState();
      const spaceId = state.activeSpace?.id;
      
      if (spaceId) {
        await state.saveToBackend();
        // Backend save completed (log removed to reduce spam)
      }
    } catch (error) {
      log.error('Backend save failed:', error);
    } finally {
      backendSaveQueue.pending = false;
    }
  },
  
  flush: () => {
    if (backendSaveQueue.timer) {
      clearTimeout(backendSaveQueue.timer);
      backendSaveQueue.timer = null;
    }
    return backendSaveQueue.execute();
  }
};

// Create debounced save for drag operations specifically
const debouncedPersistStore = debounce(() => {
  log.info('Saving to backend after drag debounce');
  backendSaveQueue.execute();
}, 500); // 500ms debounce for drag operations

// Add debounced store update to prevent excessive updates during initialization
const debouncedStoreUpdate = debounce((updateFn: () => void) => {
  updateFn();
}, 100); // 100ms debounce for CRDT updates

export type SpaceStatus = 'idle' | 'loading' | 'loaded' | 'error';

export interface WindowState {
  position: { x: number; y: number };
  size: { width: number; height: number };
}

// ✅ NORMALIZED: Window with entity references only (no embedded entities)
export interface NormalizedWindowEntity extends WindowEntity {
  entityId?: string; // Reference to entity ID (_id field)
  entityLlmId?: string; // Reference to entity llmId for fast lookup
  entity?: any; // ✅ Direct entity reference (used for custom apps with App entity containing source code)
}

export interface SpaceState {
  status: SpaceStatus;
  error?: Error;
  activeSpace: Space | null;
  windows: Record<string, NormalizedWindowEntity>; // ✅ SURGICAL FIX: Now uses normalized windows
  // ✅ REFACTOR: entities removed - use entityStore as single source of truth
  contextEntities: Record<string, { llmId: string; windowId?: string; isManuallySet: boolean; addedAt: number }>; // ✅ NEW: Persistent context state
  contextExcerpts: any[]; // ✅ NEW: Persistent excerpts
  selectedIds: string[];
  initialized: boolean;
  activeWindowId: string | null;
  layout?: any;
  cleanupFn: () => void;
  initializing: boolean;
  currentSpaceId?: string;
  initializationStatus: 'idle' | 'initializing' | 'initialized' | 'error';
  lifecycle: 'uninitialized' | 'loading' | 'ready' | 'error';  // ✅ NEW: Formal lifecycle state
  isChatVisible: boolean;
  loadedWindowIds: Set<string>;  // ✅ PERFORMANCE: Track which windows have been mounted (lazy loading)
  
  // Global page context awareness
  pageType: 'home' | 'space' | 'standalone' | null;
  isStandaloneMode: boolean;
  activeAppId: string | null; // NEW: Track which app is active in standalone mode (for dynamic tool loading)
  
  // Sidebar state
  isSidebarVisible: boolean;
  sidebarWidth: number;
  sidebarMode: 'normal' | 'overlay';
  
  // Chat expand state
  isChatExpanded: boolean;
  previousLayoutState: {
    sidebarVisible: boolean;
    sidebarWidth: number;
    chatWidth: number;
  } | null;
  
  // Dev features toggle
  hideDevFeatures: boolean;
  
  // Internal state for triggering updates
  _refreshTimestamp?: number;
  
  // Actions
  setIsChatVisible: (isChatVisible: boolean) => Promise<void>;
  setSidebarVisible: (isVisible: boolean) => Promise<void>;
  setSidebarWidth: (width: number) => Promise<void>;
  setSidebarMode: (mode: 'normal' | 'overlay') => Promise<void>;
  setChatWidth: (width: number) => Promise<void>;
  toggleChatExpanded: () => Promise<void>;
  toggleHideDevFeatures: () => void;
}

export interface SpaceStore extends SpaceState {
  // Internal mutation helpers (single code path for all mutations)
  _applyLocalWindowUpdate: (windowId: string, updates: Partial<NormalizedWindowEntity>, opts?: {
    log?: boolean;
    source?: 'local' | 'crdt';
    syncCRDT?: boolean;
  }) => void;
  _applyLocalWindowDelete: (windowId: string, opts?: {
    log?: boolean;
    source?: 'local' | 'crdt';
    syncCRDT?: boolean;
    nextActiveWindowId?: string | null;
  }) => void;
  
  // Window actions (canonical mutation API)
  actions: {
    createWindow: (params: {
      appType: string;
      title?: string;
      entity?: any;
      position?: { x: number; y: number };
      size?: { width: number; height: number };
      applicationState?: any;
      props?: Record<string, any>;
    }) => Promise<string>;
    updateWindow: (windowId: string, updates: Partial<NormalizedWindowEntity>) => Promise<void>;
    closeWindow: (windowId: string) => void;
    setActiveWindow: (windowId: string | null) => void;
    navigateWindow: (windowId: string, entityId: string) => Promise<void>;
    navigateBack: (windowId: string) => Promise<void>;
    navigateForward: (windowId: string) => Promise<void>;
    clearWindowEntityId: (windowId: string, fallbackTitle?: string) => void;
  };
  
  getSpaceStatus: () => Promise<SpaceStatus>;
  initialize: (spaceId: string) => Promise<void>;
  setPageContext: (pageType: 'home' | 'space' | 'standalone', spaceId?: string, appId?: string) => void;
  setActiveApp: (appId: string | null) => void; // NEW: Explicitly set active app for tool loading
  addWindow: (window: WindowEntity) => void;
  removeWindow: (windowId: string) => void;
  updateWindow: (windowId: string, updates: Partial<AnyWindowEntity>) => void;
  selectWindow: (windowId: string, isMultiSelect: boolean) => void;
  groupSelected: () => void;
  ungroup: (windowId: string) => void;
  setWindows: (windows: AnyWindowEntity[]) => void;
  setStatus: (status: SpaceStatus) => void;
  cleanup: () => void;
  updateSpaceName: (newName: string) => Promise<void>;
  setBackground: (background: SpaceBackground) => Promise<void>;
  showDebugger: boolean;
  toggleDebugger: () => void;
  setActiveWindow: (windowId: string | null) => void;
  bringWindowToFront: (windowId: string) => void;
  setViewMode: (mode: 'spatial' | 'focused' | 'gui2' | 'home' | 'canvas') => Promise<void>;
  saveToBackend: () => Promise<void>;
  updateSpaceChatSettings: (spaceChatUpdates: Partial<NonNullable<SpaceSettings['spaceChat']>>) => Promise<void>;
  updateEntitySidebarSettings: (sidebarSettings: Partial<NonNullable<SpaceSettings['entitySidebar']>>) => Promise<void>;
  
  // Entity access
  refreshWindowEntityFromBackend: (windowId: string) => Promise<void>;
  getWorkspaceEntities: () => Array<{ llmId: string; name: string; type: string; windowId: string; entity: any }>;
  
  // ✅ SURGICAL FIX: New normalized entity access methods
  getWindowEntity: (windowId: string) => any | null; // Get entity for a specific window
  getWindowWithEntity: (windowId: string) => AnyWindowEntity | null; // Backward compatibility - returns window with embedded entity
  getAllWindowsWithEntities: () => Record<string, AnyWindowEntity>; // Backward compatibility - returns all windows with embedded entities
  // ✅ REFACTOR: setEntity removed - entityStore is single source of truth
  updateEntityEverywhere: (entityId: string, updates: Partial<any>) => Promise<any>; // NEW: Update entity across all stores and components
  fixWindowEntityAssociation: (windowId: string) => Promise<void>; // Fix broken entity associations
  fixAllWindowEntityAssociations: () => Promise<void>; // Fix all broken entity associations
  
  // ✅ NEW: Context management methods
  addToContext: (llmId: string, isManuallySet?: boolean) => void; // Add entity to persistent context
  removeFromContext: (llmId: string) => void; // Remove entity from persistent context
  getContextEntities: () => Record<string, { llmId: string; isManuallySet: boolean; addedAt: number }>; // Get current context state
  syncContextWithContextStore: () => void; // Sync with contextStore
  
  // ✅ PERFORMANCE: Lazy window loading methods
  markWindowAsLoaded: (windowId: string) => void; // Mark window as loaded (mounted)
  getLoadedWindowIds: () => Set<string>; // Get all loaded window IDs
  // Excerpts persistence
  addExcerpt: (excerpt: any) => void;
  removeExcerpt: (index: number) => void;
  clearExcerpts: () => void;
  removeContextEntity: (llmId: string) => void;
  
  // Cleanup methods
  cleanupStaleWindows: () => Promise<void>;
  forceSyncCRDTWithLocalState: () => Promise<void>;
  
  // New hierarchical operations
  moveWindowToGroup: (windowId: string, targetGroupId: string | null, position: 'above' | 'below' | 'inside') => Promise<void>;
  createGroupFromWindows: (windowIds: string[], groupName?: string) => Promise<string | null>;
  ungroupWindows: (groupId: string) => Promise<boolean>;
  updateWindowOrder: (windowIds: string[], parentGroupId?: string | null) => Promise<void>;
  updateGroupCollapsedState: (groupId: string, isCollapsed: boolean) => Promise<void>;
}

export const useSpaceStore = create<SpaceStore>((set, get) => {
  /**
   * Internal helper: Apply local window update (create or update)
   * This is the SINGLE code path for all window mutations
   */
  const _applyLocalWindowUpdate = (
    windowId: string,
    updates: Partial<NormalizedWindowEntity>,
    opts?: {
      log?: boolean;
      source?: 'local' | 'crdt';
      syncCRDT?: boolean;
    }
  ): void => {
    const options = {
      log: true,
      source: 'local' as const,
      syncCRDT: true,
      ...opts
    };
    
    // ✅ RUNTIME GUARDS: Prevent critical bugs
    
    // 🔴 CRITICAL GUARD: Prevent CRDT infinite loop (browser crash)
    // CRDT updates MUST NOT sync back to CRDT (creates infinite loop)
    if (options.source === 'crdt' && options.syncCRDT !== false) {
      const errorMsg = `[spaceStore] 🔄 CRDT LOOP PREVENTED! CRDT updates must set syncCRDT=false. Window: ${windowId}`;
      console.error(errorMsg, {
        windowId,
        source: options.source,
        syncCRDT: options.syncCRDT,
        stack: new Error().stack
      });
      // THROW to prevent infinite loop - this is critical
      throw new Error(errorMsg);
    }
    
    // Guard: Window should have required fields
    if (!windowId) {
      console.error('[spaceStore] ⚠️ RUNTIME GUARD: windowId is required');
      throw new Error('[spaceStore] windowId is required for window update');
    }
    
    const currentState = get();
    const existingWindow = currentState.windows[windowId];
    const spaceId = currentState.activeSpace?.id;
    
    // PERFORMANCE: Check if updates would actually change anything BEFORE merging
    if (existingWindow) {
      let hasRealChanges = false;
      for (const [key, value] of Object.entries(updates)) {
        const existingValue = (existingWindow as any)[key];
        
        // Deep equality for objects like position and size
        if (typeof value === 'object' && value !== null && typeof existingValue === 'object' && existingValue !== null) {
          if (JSON.stringify(value) !== JSON.stringify(existingValue)) {
            hasRealChanges = true;
            break;
          }
        } else if (existingValue !== value) {
          hasRealChanges = true;
          break;
        }
      }
      
      // If nothing actually changed, skip the entire update
      if (!hasRealChanges) {
        return;
      }
    }
    
    // Merge with existing window or create new
    const updatedWindow: NormalizedWindowEntity = existingWindow 
      ? { ...existingWindow, ...updates, id: windowId }
      : { ...updates, id: windowId } as NormalizedWindowEntity;
    
    // TODO: Add undo/redo support for windows in the future
    // if (options.log && options.source === 'local') {
    //   // Log to undo stack
    // }
    
    // ✅ REFACTOR: No entity sync needed - entityStore is single source of truth
    // Windows only store references (entityLlmId), not embedded entities
    
    // Update state (we already checked for changes above)
    set((state) => ({
      windows: {
        ...state.windows,
        [windowId]: updatedWindow
      }
    }));
    
    // Only sync to CRDT if it's a local action
    if (options.syncCRDT && options.source === 'local' && spaceId) {
      // ✅ CRITICAL FIX: Update CRDT immediately to prevent race condition
      // The CRDT observer deletes windows that exist in store but not in CRDT,
      // so we must sync to CRDT before any CRDT sync event can trigger
      spaceCRDTService.updateWindow(spaceId, windowId, updatedWindow);
      
      // Also persist to backend
      const isNewWindow = !existingWindow;
      if (isNewWindow) {
        // Persist immediately for new windows
        backendSaveQueue.flush();
      } else {
        // For updates, use delayed batch save
        backendSaveQueue.schedule(500);
      }
    }
  };
  
  /**
   * Internal helper: Apply local window delete
   * This is the SINGLE code path for all window deletions
   */
  const _applyLocalWindowDelete = (
    windowId: string,
    opts?: {
      log?: boolean;
      source?: 'local' | 'crdt';
      syncCRDT?: boolean;
      nextActiveWindowId?: string | null;
    }
  ): void => {
    const options = {
      log: true,
      source: 'local' as const,
      syncCRDT: true,
      ...opts
    };
    
    const currentState = get();
    const spaceId = currentState.activeSpace?.id;
    
    if (options.syncCRDT && options.source === 'local' && spaceId) {
      spaceCRDTService.removeWindow(spaceId, windowId);
      backendSaveQueue.schedule(500);
    }
    
    set((state) => {
      const { [windowId]: removed, ...remainingWindows } = state.windows;
      
      const newLoadedSet = new Set(state.loadedWindowIds);
      newLoadedSet.delete(windowId);
      
      const isActiveBeingDeleted = state.activeWindowId === windowId;
      let newActiveWindowId = state.activeWindowId;
      if (isActiveBeingDeleted) {
        newActiveWindowId = options.nextActiveWindowId !== undefined
          ? options.nextActiveWindowId
          : null;
      }
      
      return {
        windows: remainingWindows,
        loadedWindowIds: newLoadedSet,
        activeWindowId: newActiveWindowId
      };
    });
  };
  
  return {
  status: 'idle',
  activeSpace: null,
  windows: {},
  // ✅ REFACTOR: entities removed - use entityStore as single source of truth
  contextEntities: {}, // ✅ NEW: Persistent context state - tracks which entities are in context
  contextExcerpts: [],
  selectedIds: [],
  initialized: false,
  initializing: false,
  activeWindowId: null,
  layout: undefined,
  cleanupFn: () => {},
  currentSpaceId: undefined,
  initializationStatus: 'idle',
  lifecycle: 'uninitialized',  // ✅ NEW: Initial lifecycle state
  showDebugger: false,
  isChatVisible: true, // ✅ Default to visible for new users
  loadedWindowIds: new Set<string>(),  // ✅ PERFORMANCE: Track mounted windows for lazy loading
  
  // Global page context awareness
  pageType: null,
  isStandaloneMode: false,
  activeAppId: null, // NEW: Track which app is active in standalone mode (for dynamic tool loading)
  
  // Sidebar state
  isSidebarVisible: false, // Default to closed
  sidebarWidth: 300,
  sidebarMode: 'normal',
  
  // Chat expand state
  isChatExpanded: false,
  previousLayoutState: null,
  
  // Dev features toggle - start with false to show dev features by default in development
  hideDevFeatures: false,
  
  // Set page context from anywhere (SystemContext, layouts, etc.)
  setPageContext: (pageType, spaceId, appId) => {
    const currentState = get();
    
    console.log('[spaceStore.setPageContext] Setting page context:', {
      pageType,
      spaceId,
      appId,
      previousAppId: currentState.activeAppId,
      willClearAppId: !appId && pageType !== 'standalone'
    });
    
    set((state) => ({
      ...state,
      pageType,
      isStandaloneMode: pageType === 'standalone',
      currentSpaceId: spaceId ?? state.currentSpaceId,
      // ✅ IMPORTANT: Only update activeAppId if explicitly provided
      // This preserves app context across route changes
      activeAppId: appId !== undefined ? appId : state.activeAppId,
    }));
    
    console.log('[spaceStore.setPageContext] ✅ Page context updated:', {
      activeAppId: get().activeAppId,
      isStandaloneMode: get().isStandaloneMode
    });
  },
  
  // NEW: Explicitly enable/disable app tools
  setActiveApp: (appId) => {
    console.log('[spaceStore.setActiveApp] Setting active app:', { appId });
    set({ activeAppId: appId });
  },

  // Actions
  setIsChatVisible: async (isChatVisible: boolean) => {
    const currentState = get();
    // No-op if value unchanged — avoids a PATCH /api/spaces/{id} on every page load
    if (currentState.isChatVisible === isChatVisible) return;

    // Update local state immediately for UI responsiveness
    set({ isChatVisible });
    
    // Persist to backend
    const state = get();
    const spaceId = state.activeSpace?.id;
    const activeSpace = state.activeSpace;
    
    if (spaceId && activeSpace) {
      const newSettings = {
        ...activeSpace.settings,
        isChatVisible
      };
      
      try {
        // Update backend first (include activeWindowId)
      await spaceService.updateSpace(spaceId, activeSpace.windows, newSettings, state.activeWindowId, state.contextEntities, state.contextExcerpts);
        log.log('[SpaceStore] Successfully updated isChatVisible in backend');
        
        // Update local activeSpace to keep in sync
        set((state) => ({
          activeSpace: state.activeSpace ? {
            ...state.activeSpace,
            settings: newSettings
          } : null
        }));
        
        // Also update CRDT for real-time collaboration
        spaceCRDTService.updateSettings(spaceId, newSettings);
        
        // ✅ PROPER ORCHESTRATION: Update systemStore cache to keep UI in sync
        const useSystemStore = (await import('./systemStore')).default;
        const updatedSpace = {
          ...activeSpace,
          settings: newSettings,
          updatedAt: new Date().toISOString()
        };
        useSystemStore.getState()._applyLocalSpaceUpdate(updatedSpace, {
          source: 'local',
          log: false
        });
      } catch (error) {
        log.error('[SpaceStore] Error updating isChatVisible in backend:', error);
      }
    }
  },

  toggleHideDevFeatures: () => set(state => ({ hideDevFeatures: !state.hideDevFeatures })),

  getSpaceStatus: async () => {
    return get()?.status;
  },

  initialize: async (spaceId: string) => {
    // ✅ MOVED: Command registration now happens globally in App.tsx during bootstrap
    // Commands must be available BEFORE any space initialization to support workflows
    // We keep the contextStore subscription initialization here since it's space-specific
    if (!get().initializing) {
      set({ initializing: true });
      
      try {
        // Initialize context store subscription after stores are ready
        const { initializeContextStoreSubscription } = await import('../components/Space/SpaceChat/store/contextStore');
        initializeContextStoreSubscription();
        log.info('✅ [SpaceStore] Context store subscription initialized');
      } catch (error) {
        log.error('❌ [SpaceStore] Failed to initialize context store subscription:', error);
        set({ initializing: false });
        throw error;
      }
      
      // ✅ NOTE: EntityStore initialization is now handled upfront in SpacePageWrapper
      // to avoid race conditions during rehydration. See SpacePageWrapper.tsx line ~170
    } else {
      log.info('⏭️ [SpaceStore] Context store subscription already initialized, skipping');
    }

    const state = get();
    log.info('🔵 Starting initialization:', {
      requestedSpaceId: spaceId,
      currentSpaceId: state.currentSpaceId,
      initStatus: state.initializationStatus,
      initialized: state.initialized,
      hasActiveSpace: !!state.activeSpace,
      activeSpaceId: state.activeSpace?.id
    });

    // We also maintain this variable separately from state to avoid race conditions
    if (window.__SPACE_INITIALIZING__ === true) {
      log.info('Already initializing (global lock)');
      
      // Return a promise that resolves when the current initialization is done
      return new Promise<void>((resolve) => {
        let attempts = 0;
        const checkDone = () => {
          attempts++;
          if (window.__SPACE_INITIALIZING__ !== true) {
            log.info('Global lock released, continuing');
            resolve();
          } else if (attempts > 50) { // 5 seconds timeout
            log.info('Timeout waiting for lock, forcing release');
            window.__SPACE_INITIALIZING__ = false;
            resolve();
          } else {
            setTimeout(checkDone, 100);
          }
        };
        setTimeout(checkDone, 100);
      });
    }

    if (state.initializationStatus === 'initializing') {
      log.log('[SpaceStore] Already initializing');
      return;
    }

    // Detect a space switch by checking spaceCRDTService.initializedSpaces instead of
    // state.currentSpaceId. The problem: setPageContext() sets state.currentSpaceId to
    // the NEW spaceId BEFORE initializeSpace() runs, so the old check
    // (state.currentSpaceId !== spaceId) was always false — the cleanup block was
    // never entered and the old space's CRDT observer was never disconnected.
    // spaceCRDTService.initializedSpaces is only modified by disconnectSpace/initializeSpace,
    // so it correctly reflects which space has an active CRDT connection.
    const prevCrdtSpaceId = Array.from(spaceCRDTService.initializedSpaces).find(id => id !== spaceId);
    if (prevCrdtSpaceId) {
      const isSpaceSwitch = true;
      const oldSpaceId = prevCrdtSpaceId;
      
      log.log(`[SpaceStore] 🔄 Resetting space store:`, {
        reason: `switching from ${oldSpaceId} to ${spaceId}`,
        detectedVia: 'spaceCRDTService.initializedSpaces',
        currentSpaceId: state.currentSpaceId,
        requestedSpaceId: spaceId,
        initStatus: state.initializationStatus
      });
      
      // CRITICAL: Clean up old space's CRDT connections before resetting
      const { cleanupFn } = state;
      if (cleanupFn) {
        log.info('[SpaceStore] 🧹 Calling cleanup function for old space');
        cleanupFn();
      }
      
      // CRITICAL: If switching spaces, clean up the old space's CRDT observers
      if (isSpaceSwitch && oldSpaceId) {
        log.info(`[SpaceStore] 🧹 Disconnecting CRDT for old space: ${oldSpaceId}`);
        try {
          // Disconnect the specific space instead of disconnectAll (which would affect all spaces)
          spaceCRDTService.disconnectSpace(oldSpaceId, 'space-switch');
        } catch (error) {
          log.error('[SpaceStore] Error disconnecting old space CRDT:', error);
        }
      }
      
      // Reset state but keep the basic structure
      set({ 
        status: 'idle',
        activeSpace: null,
        windows: {},
        // ✅ REFACTOR: entities removed - use entityStore as single source of truth
        contextEntities: {}, // ✅ NEW: Reset context on space switch
        initialized: false,
        selectedIds: [],
        activeWindowId: null,
        cleanupFn: () => {},
        initializationStatus: 'idle',  // Reset initialization status
        lifecycle: 'uninitialized',  // ✅ NEW: Reset lifecycle
        currentSpaceId: undefined       // Clear current space ID
      });
    }

    try {
      // Set global initialization lock
      window.__SPACE_INITIALIZING__ = true;
      set({ initializationStatus: 'initializing', lifecycle: 'loading' });
      
      // Get the space from the backend
      const existingSpace = await spaceService.getSpace(spaceId);
      // No need to delete created/updated as UISpace now uses createdAt/updatedAt
      if (existingSpace) {
        log.log('[SpaceStore] 📦 Fetched space from backend');
        log.log('[SpaceStore] 📦 FULL SPACE ENTITY:', JSON.stringify(existingSpace, null, 2));
        log.log('[SpaceStore] 📦 Space summary:', {
          id: existingSpace.id,
          name: existingSpace.name,
          org_id: existingSpace.org_id,
          windowCount: Object.keys(existingSpace.windows || {}).length,
          hasSettings: !!existingSpace.settings,
          hasContextEntities: !!existingSpace.contextEntities,
          createdAt: existingSpace.createdAt,
          updatedAt: existingSpace.updatedAt
        });
      } else {
        log.log('[SpaceStore] No space found in backend');
      }
      
      // Clean up any ghost windows from the backend space
      if (existingSpace && existingSpace.windows) {
        let windowsNeedCleanup = false;
        const validWindows: Record<string, AnyWindowEntity> = {};
        
        // Check each window to see if it's valid
        Object.entries(existingSpace.windows).forEach(([windowId, window]) => {
          // Valid windows must have id, type, position, and size
          // Exception: group windows (appType: 'group') are always valid even if they have minimal properties
          if (window && 
              window.id && 
              window.type === 'window' && 
              (window.appType === 'group' || (window.position && window.size))) {
            validWindows[windowId] = window;
          } else {
            // This is a ghost window
            windowsNeedCleanup = true;
            log.log(`[SpaceStore] Found ghost window during init: ${windowId}`, window);
          }
        });
        
        // If we found ghost windows, replace with cleaned version
        if (windowsNeedCleanup) {
          log.log(`[SpaceStore] Cleaning up ${Object.keys(existingSpace.windows).length - Object.keys(validWindows).length} ghost windows`);
          existingSpace.windows = validWindows;
          
          // Also persist the cleaned windows to the backend
          try {
            await spaceService.updateSpace(spaceId, validWindows, existingSpace.settings || {}, existingSpace.activeWindowId, get().contextEntities, get().contextExcerpts);
            log.log('[SpaceStore] Successfully persisted cleaned windows to backend');
          } catch (error) {
            log.error('[SpaceStore] Error persisting cleaned windows:', error);
          }
        }
      }
      
      const newSpace = createUISpace(spaceId);
      
      // Use backend data or create new space - let CRDT service handle merging
      const space = existingSpace || newSpace;
      
      // CRDT service handles merging with existing CRDT data internally
      // Only initialize if not already initialized (prevents duplicate observers in Strict Mode)
      try {
        await spaceCRDTService.initializeSpace(spaceId, space);
      } catch (error) {
        // If initialization was skipped (already initialized), that's fine
        log.log('[SpaceStore] CRDT initialization result:', error);
      }
      
      // ✅ REFACTOR: Normalize loaded space windows (no entity extraction needed)
      log.info('[SpaceStore] 🔄 Normalizing loaded space windows');
      const normalizedWindows: Record<string, NormalizedWindowEntity> = {};
      
      if (space.windows) {
        Object.entries(space.windows).forEach(([windowId, windowEntity]) => {
          // Create normalized window structure
          const normalizedWindow: NormalizedWindowEntity = {
            ...windowEntity,
            // ✅ CRITICAL: Preserve existing entityId/entityLlmId from backend, don't overwrite with embedded entity
            entityId: windowEntity.entityId || windowEntity.entity?._id || windowEntity.entity?.id,
            entityLlmId: windowEntity.entityLlmId || windowEntity.entity?.llmId,
            // ✅ FIXED: Remove embedded entity to complete normalization
            entity: undefined
          };
          
          normalizedWindows[windowId] = normalizedWindow;
        });
        
        log.info('[SpaceStore] 🎯 Space normalization complete:', {
          totalWindows: Object.keys(normalizedWindows).length
        });
        
        // Update space with normalized windows
        space.windows = normalizedWindows;
        
        // ✅ REFACTOR: Removed auto-add document to context event dispatch
        // The contextStore subscription to activeWindowId will handle this automatically
      }

      // ✅ CRITICAL FIX: Handle windows that don't have embedded entities but have applicationState
      // This fixes the issue where windows lose entity references during space initialization
      const { getWindowEntityId } = await import('../components/Space/utils/windowUtils');
      const windowsNeedingEntityAssociation = Object.keys(space.windows || {}).filter(windowId => {
        const window = space.windows![windowId] as NormalizedWindowEntity;
        return !window.entityLlmId && getWindowEntityId(window);
      });
      
      if (windowsNeedingEntityAssociation.length > 0) {
        log.info('[SpaceStore] 🔧 Found windows needing entity association:', {
          count: windowsNeedingEntityAssociation.length,
          windowIds: windowsNeedingEntityAssociation
        });
      }
      
      // Set a flag to indicate we're in initial sync
      let isInitialSync = true;
      
      // ✅ PROPER FIX: Subscribe to entityStore changes to keep window entities fresh
      const entityStoreModule = await import('./entityStore');
      const useEntityStore = entityStoreModule.default;
      const unsubscribeEntityStore = useEntityStore.subscribe((state: any, prevState: any) => {
        // Only process if entities actually changed
        if (state.entities === prevState.entities) return;
        
        const entities = state.entities;
        const currentWindows = get().windows;
        let anyWindowUpdated = false;
        
        Object.entries(currentWindows).forEach(([windowId, window]) => {
          if (window.entityId) {
            const freshEntity = entities.find((e: any) => e._id === window.entityId);
            
            if (freshEntity) {
              // ✅ FIX: Compare window.title vs entity.name (not currentEntity.name vs freshEntity.name)
              // This catches cases where the entity was updated but the window title wasn't synced
              if (window.title !== freshEntity.name) {
                // Update window with fresh entity metadata
                get()._applyLocalWindowUpdate(windowId, {
                  title: freshEntity.name,
                  entity: {
                    _id: freshEntity._id,
                    id: freshEntity._id,
                    name: freshEntity.name,
                    entityType: freshEntity.entityType,
                    llmId: freshEntity.llmId,
                    updated_at: freshEntity.updated_at
                  }
                }, {
                  source: 'local', // Treat as local mutation (came from entityStore)
                  syncCRDT: true, // Sync to other tabs
                  log: false
                });
                
                anyWindowUpdated = true;
                
                log.info('[SpaceStore] ✅ Synced window entity from entityStore:', {
                  windowId,
                  entityId: freshEntity._id,
                  newName: freshEntity.name
                });
              }
            }
          }
        });
        
        if (anyWindowUpdated) {
          log.info('[SpaceStore] Window entities synced with fresh data from entityStore');
        }
      });

      // Set up observer after initialization is complete
      const cleanupFn = spaceCRDTService.observeSpace(spaceId, (update) => {
        log.log('[SpaceStore][update path] Received space update, windows count:', 
          Object.keys(update.windows || {}).length);
        
        // ✅ CRITICAL FIX: Merge partial CRDT updates with existing window state
        // CRDT sends partial updates (only changed properties), not complete windows!
        // Start with existing windows, then apply CRDT updates
        const currentState = get();
        const filteredWindows: Record<string, any> = { ...currentState.windows };
        
        log.info(`[SpaceStore] 🔄 Starting merge: ${Object.keys(filteredWindows).length} existing windows`);
        
        for (const [windowId, partialWindow] of Object.entries(update.windows || {})) {
          const existingWindow = filteredWindows[windowId];
          
          if (existingWindow) {
            // Merge partial update with existing window (deep merge)
            const mergedWindow = {
              ...existingWindow,
              ...partialWindow,
              applicationState: {
                ...existingWindow.applicationState,
                ...partialWindow.applicationState
              }
            };
            
            // Log if entityId or entityLlmId changed during merge
            if (partialWindow.entityId || partialWindow.entityLlmId) {
              log.info(`[SpaceStore] 🔄 CRDT merge modifying entity references:`, {
                windowId,
                title: mergedWindow.title,
                before: {
                  entityId: existingWindow.entityId,
                  entityLlmId: existingWindow.entityLlmId
                },
                after: {
                  entityId: mergedWindow.entityId,
                  entityLlmId: mergedWindow.entityLlmId
                },
                crdtUpdate: {
                  entityId: partialWindow.entityId,
                  entityLlmId: partialWindow.entityLlmId
                }
              });
            }
            
            filteredWindows[windowId] = mergedWindow;
            log.info(`[SpaceStore] 🔄 Merged CRDT update with existing window: ${windowId}`);
          } else if (partialWindow.id && partialWindow.type === 'window') {
            // New window with complete properties - add it
            filteredWindows[windowId] = partialWindow;
            log.info(`[SpaceStore] ➕ Added new complete window from CRDT: ${windowId}`);
          } else {
            // Incomplete new window - skip it (don't add to filteredWindows)
            log.warn(`[SpaceStore] ⚠️ Skipping incomplete new window from CRDT:`, {
              windowId,
              hasWindow: !!partialWindow,
              hasId: !!partialWindow?.id,
              type: partialWindow?.type,
              reason: 'incomplete_new_window_from_crdt'
            });
          }
        }
        
        log.info(`[SpaceStore] 🔄 After merge: ${Object.keys(filteredWindows).length} total windows`);
        
        const removingWindows = spaceCRDTService.getRemovingWindows();
        let filteredCount = 0;
        
        // Remove ONLY windows explicitly marked for removal
        // ✅ FIX: Don't assume missing windows were "removed locally" - they might be NEW windows!
        removingWindows.forEach((windowId: string) => {
          if (filteredWindows[windowId]) {
            log.log(`[SpaceStore] Filtering out window ${windowId} from CRDT update - removal in progress`);
            delete filteredWindows[windowId];
            filteredCount++;
          }
        });
        
        if (filteredCount > 0) {
          log.log(`[SpaceStore] Filtered ${filteredCount} windows from CRDT update`);
        }
        
        // Add robust retry logic for the space update
        const updateWithRetry = async (retries = 5, delay = 1000) => {
          for (let attempt = 0; attempt < retries; attempt++) {
            try {
              await spaceService.updateSpace(spaceId, filteredWindows || {}, update.settings || {}, update.activeWindowId, get().contextEntities, get().contextExcerpts);
              if (attempt > 0) {
                log.log(`[SpaceStore] Update succeeded on retry attempt ${attempt + 1}`);
              }
              break; // Success, exit the retry loop
            } catch (error) {
              const isLast = attempt === retries - 1;
              const is404 = error instanceof Error && error.message.includes('404');
              
              log.warn(`[SpaceStore] Update attempt ${attempt + 1}/${retries} failed:`, error);
              
              // For 404 errors, be more aggressive with retries since they might indicate database sync issues
              if (is404 && !isLast) {
                log.log(`[SpaceStore] 404 error detected - retrying with longer delay (space might be syncing in database)`);
                // Use longer delay for 404s to allow database synchronization
                await new Promise(resolve => setTimeout(resolve, delay * 2));
                delay *= 1.5; // Gentler exponential backoff for 404s
                continue;
              }
              
              // For other errors, use normal retry logic
              if (!isLast) {
                await new Promise(resolve => setTimeout(resolve, delay));
                delay *= 2;
                continue;
              }
              
              // Last attempt failed - log but don't throw since local state should still work
              log.error('[SpaceStore] All retry attempts failed - continuing with local state only:', error);
              break;
            }
          }
        };
        
        // Trigger the update with retry
        updateWithRetry().catch(error => {
          log.error('[SpaceStore] Failed to persist space update:', error);
          // Continue anyway - local state should still be updated
        });
        
        // Use debounced update to prevent excessive store updates during initialization
        debouncedStoreUpdate(() => {
          const currentState = get();
          
          // Skip update if windows haven't actually changed (avoid redundant updates)

          const newWindowKeys = Object.keys(filteredWindows || {}).sort();
          const newActiveWindowId = update.activeWindowId;
          
          // Deep equality check for windows to detect actual changes
          const windowsChanged = !isEqual(currentState.windows, filteredWindows);
          const activeWindowChanged = currentState.activeWindowId !== newActiveWindowId;
          const settingsChanged = !isEqual(currentState.activeSpace?.settings, update.settings);
          
          // Only update if something actually changed
          if (windowsChanged || activeWindowChanged || settingsChanged) {
            log.log('[SpaceStore] CRDT update triggered store change:', {
              windowsChanged,
              activeWindowChanged: activeWindowChanged ? `${currentState.activeWindowId} -> ${newActiveWindowId}` : false,
              settingsChanged,
              windowCount: newWindowKeys.length
            });
            
            // 🛡️ CRITICAL FIX: Only create new objects for fields that actually changed
            // This prevents unnecessary re-renders in components subscribing to these fields
            const newState: any = {
              ...state
            };
            
            // Only update windows if they actually changed (prevents cascading re-renders)
            if (windowsChanged) {
              newState.windows = filteredWindows || {};
            }
            
            // Update activeWindowId if changed
            if (activeWindowChanged) {
              newState.activeWindowId = isInitialSync 
                ? (state.activeWindowId || newActiveWindowId)
                : (state.activeWindowId && filteredWindows[state.activeWindowId] 
                  ? state.activeWindowId 
                  : (newActiveWindowId && filteredWindows[newActiveWindowId] ? newActiveWindowId : null));
            }
            
            // Update isChatVisible if settings changed
            if (settingsChanged) {
              newState.isChatVisible = update.settings?.isChatVisible ?? state.isChatVisible;
              // Only update activeSpace object if settings changed (to prevent unnecessary re-renders)
              newState.activeSpace = { ...update, windows: filteredWindows };
            }
            
            // Update local state with only the fields that changed
            set(newState);
            
            // Clear initial sync flag after first update
            if (isInitialSync) {
              isInitialSync = false;
              log.log('[SpaceStore] Initial CRDT sync complete');
            }
          } else {
            log.log('[SpaceStore] Skipping redundant CRDT update - no changes detected');
          }
        });
      });

      // ✅ ENHANCED: Load activeWindowId with priority: localStorage > backend > first window
      const localStorageWindowId = localStorage.getItem(`activeWindowId_${spaceId}`);
      const backendWindowId = space.activeWindowId;
      const firstWindowId = Object.keys(space.windows || {})[0];
      
      log.log('[SpaceStore] Determining activeWindowId on init:', {
        localStorageWindowId,
        backendWindowId,
        firstWindowId,
        availableWindows: Object.keys(space.windows || {}).length
      });
      
      // Priority: localStorage (most recent) > backend (persisted) > first window (fallback)
      let activeWindowId = localStorageWindowId || backendWindowId || firstWindowId || null;
      
      // Validate that the selected window still exists
      if (activeWindowId && !(space.windows || {})[activeWindowId]) {
        log.log('[SpaceStore] Selected activeWindowId no longer exists, trying fallbacks:', {
          attempted: activeWindowId,
          tryingBackend: !!backendWindowId,
          tryingFirst: !!firstWindowId
        });
        
        // Try backend window if localStorage failed
        if (backendWindowId && space.windows[backendWindowId]) {
          activeWindowId = backendWindowId;
        } else if (firstWindowId) {
          activeWindowId = firstWindowId;
        } else {
          activeWindowId = null;
        }
      }
      
      // Set the space data with normalized entities
      log.info('[SpaceStore] 🎯 Setting initialized space state:', {
        spaceId,
        spaceName: space.name,
        windowCount: Object.keys(space.windows || {}).length,
        activeWindowId: activeWindowId,
        localStorageWindowId,
        backendWindowId,
        firstWindowId,
        source: localStorageWindowId ? 'localStorage' : backendWindowId ? 'backend' : 'firstWindow'
      });
      
      set({ 
        initialized: true,
        initializing: false, // ✅ FIX: Ensure initializing is false when initialized is true
        activeSpace: space,
        // ✅ FIX: Use ONLY backend windows when initializing a space
        // Merging with local windows causes contamination when switching spaces
        // The only windows in "local" at this point are from the previous space (wrong!)
        windows: space.windows || {},
        // ✅ REFACTOR: entities removed - use entityStore as single source of truth
        contextEntities: space.contextEntities || {}, // ✅ RESTORE: Load persisted context from backend
        contextExcerpts: space.contextExcerpts || [], // ✅ RESTORE: Load persisted excerpts from backend
        initializationStatus: 'initialized',
        lifecycle: 'ready',  // ✅ NEW: Mark as ready
        currentSpaceId: spaceId,
        cleanupFn: () => {
          cleanupFn();
          unsubscribeEntityStore();
        },
        activeWindowId: activeWindowId,
        isChatVisible: space.settings?.isChatVisible ?? true, // ✅ Default to visible for new users
        // ✨ INSTANT NAVIGATION: Use backend value directly (no default override)
        // Backend sends isSidebarVisible at top level for pending spaces
        isSidebarVisible: space.settings?.sidebar?.isVisible ?? (space.settings as any)?.isSidebarVisible ?? false,
        sidebarWidth: space.settings?.sidebar?.width || 300,
        sidebarMode: space.settings?.sidebar?.mode || 'normal',
      });
      
      // ✅ DEBUG: Log what we loaded
      console.log('[SpaceStore] 🔍 LOADED SETTINGS FROM BACKEND:', {
        isChatVisible: space.settings?.isChatVisible,
        isSidebarVisible_topLevel: (space.settings as any)?.isSidebarVisible,
        isSidebarVisible_nested: space.settings?.sidebar?.isVisible,
        viewMode: space.settings?.viewMode,
        isHomeSpace: (space.settings as any)?._isHomeSpace,
        willUseForSidebar: space.settings?.sidebar?.isVisible ?? (space.settings as any)?.isSidebarVisible ?? false,
        fullSettings: space.settings
      });
      
      // ✅ REFACTOR: No entityStore subscription needed
      // Components access entities directly from entityStore via helper methods
      // This eliminates the need for manual sync and prevents staleness bugs

      // 🔔 Check Space lifecycle hooks (triggers triggered Skills on first open, etc.)
      try {
        import('../services/spaceLifecycleService').then(({ spaceLifecycleService }) => {
          spaceLifecycleService.checkAndFireHooks(space);
        });
      } catch { /* non-fatal */ }
      
      // 🔍 REHYDRATION DEBUG: Log detailed state to diagnose initialization issues
      const activeWindowEntityLlmId = activeWindowId && space.windows?.[activeWindowId]?.entityLlmId;
      
      log.info('[SpaceStore] 🔍 REHYDRATION DEBUG:', {
        spaceId,
        activeWindowId,
        activeWindowEntityLlmId,
        windowCount: Object.keys(space.windows || {}).length,
        timestamp: new Date().toISOString()
      });
      
      // Verify activeSpace was set correctly
      const finalState = get();
      log.info('[SpaceStore] ✅ Space initialization completed:', {
        spaceId,
        spaceName: space.name,
        activeWindowId: activeWindowId,
        windowCount: Object.keys(space.windows || {}).length,
        contextEntitiesCount: Object.keys(space.contextEntities || {}).length,
        activeSpaceSet: !!finalState.activeSpace,
        activeSpaceId: finalState.activeSpace?.id,
        activeSpaceName: finalState.activeSpace?.name,
        activeSpaceOrgId: finalState.activeSpace?.org_id,
        initializationStatus: finalState.initializationStatus
      });
      
      // 🎯 CLEAN DATASPACE SUMMARY
      const dataspaceType = space.org_id ? 'ORG' : 'PERSONAL';
      const dataspaceIdentifier = space.org_id || 'personal';
      console.log(`
╔═══════════════════════════════════════════════════════════╗
║  🎯 SPACE LOADED: ${space.name.padEnd(40)}║
╠═══════════════════════════════════════════════════════════╣
║  Space ID:   ${space.id}                         ║
║  Dataspace:  ${dataspaceType.padEnd(8)} (${dataspaceIdentifier.substring(0, 24)})   ║
║  Windows:    ${Object.keys(space.windows || {}).length} open                                          ║
║  Status:     ✅ READY                                      ║
╚═══════════════════════════════════════════════════════════╝
      `);

      // ✅ CRITICAL: Trigger automatic context population after initialization
      // This ensures the active window is added to context on page reload
      // Removed setTimeout - do this immediately since stores are already initialized
      try {
        log.info('[SpaceStore] 🔄 Triggering automatic context population after initialization');
        
        // Import contextStore dynamically to avoid circular dependencies
        const { useContextStore } = await import('../components/Space/SpaceChat/store/contextStore');
        const contextStore = useContextStore.getState();
        
        // Trigger the automatic context logic by simulating a space store change
        // This will populate context with the active window if one exists
        const currentState = get();
        if (currentState.activeWindowId && currentState.windows[currentState.activeWindowId]) {
          log.info('[SpaceStore] 🎯 Active window detected on reload - triggering context population');
          
          // Force a state change to trigger the context subscription
          set((state) => ({ ...state, _refreshTimestamp: Date.now() }));
        }
        
        log.info('[SpaceStore] ✅ Automatic context population completed');
      } catch (error) {
        log.error('[SpaceStore] ❌ Failed to trigger automatic context population:', error);
      }

      // ✅ CRITICAL FIX: Fix entity associations for windows that don't have entityLlmId
      // This must happen after entityStore is initialized and store is set up
      if (windowsNeedingEntityAssociation.length > 0) {
        // Removed setTimeout - do this immediately since entityStore is already initialized
        try {
          log.info('[SpaceStore] 🔧 Fixing entity associations for windows after initialization');
          await get().fixAllWindowEntityAssociations();
          log.info('[SpaceStore] ✅ Completed fixing entity associations');
        } catch (error) {
          log.error('[SpaceStore] Failed to fix entity associations:', error);
        }
      }
      
      // ✅ NEW: Automatically clean up orphaned child windows after initialization
      // This prevents phantom windows from persisting across sessions
      try {
        log.info('[SpaceStore] 🧹 Running automatic orphaned window cleanup');
        await get().cleanupStaleWindows();
        log.info('[SpaceStore] ✅ Automatic cleanup completed');
      } catch (error) {
        log.error('[SpaceStore] Failed to clean up orphaned windows:', error);
        // Don't throw - this is a non-critical cleanup operation
      }
    } catch (err) {
      set({ 
        initializationStatus: 'error',
        lifecycle: 'error',  // ✅ NEW: Mark as error
        initializing: false 
      });
      throw err;
    } finally {
      // Release the global lock
      window.__SPACE_INITIALIZING__ = false;
    }
  },

  addWindow: (window: WindowEntity) => {
    const spaceId = get().activeSpace?.id;
    if (!spaceId) return;
    log.log('[SpaceStore][update path] Adding window:', window);
    
    // ✅ SURGICAL FIX: Create normalized window (backward compatible)
    const normalizedWindow: NormalizedWindowEntity = {
      ...window,
      entityId: (window as any).entityId,
      entityLlmId: (window as any).entityLlmId
      // entity field removed from type - use spaceStore.getWindowEntity(windowId) instead
    };
    
    // ✅ DEBUG: Log what entity references we extracted
    if (normalizedWindow.entityId || normalizedWindow.entityLlmId) {
      log.log('[SpaceStore] addWindow - extracted entity references:', {
        windowId: window.id,
        entityId: normalizedWindow.entityId,
        entityLlmId: normalizedWindow.entityLlmId,
        directEntityId: (window as any).entityId,
        directEntityLlmId: (window as any).entityLlmId
      });
    }

    log.log('Adding normalized window to store:', normalizedWindow);
    
    // ✅ NEW: Use helper instead of direct state mutation
    get()._applyLocalWindowUpdate(window.id, normalizedWindow, {
      source: 'local',
      syncCRDT: true,
      log: true
    });

    // Only set as active window if this is not during space initialization
    // and there's no currently active window AND this isn't from a CRDT update
    const isFromCRDTUpdate = (window as any).__fromCRDTUpdate;
    const shouldSetActive = get().initializationStatus === 'initialized' && 
                           !get().activeWindowId && 
                           !isFromCRDTUpdate;

    if (shouldSetActive) {
      set({ activeWindowId: window.id });
    }
  },

  removeWindow: (windowId: string) => {
    const spaceId = get().activeSpace?.id;
    if (!spaceId) return;
    
    const currentState = get();
    log.log('[SpaceStore][update path] Removing window:', {
      windowId,
      wasActiveWindow: currentState.activeWindowId === windowId,
      currentActiveWindow: currentState.activeWindowId,
      totalWindows: Object.keys(currentState.windows).length,
      windowIds: Object.keys(currentState.windows)
    });

    // ⭐⭐⭐ AUTO-DELETE UNUSED DOCUMENTS (NEW CODE) ⭐⭐⭐
    const windowToDelete = currentState.windows[windowId];
    
    // Get entity from applicationState first (more reliable than window.entity)
    const docState = windowToDelete?.applicationState?.docs as any;
    const entityId = (windowToDelete?.applicationState?.docs as any)?.activeDocId || windowToDelete?.entityId;
    
    log.info('[SpaceStore][AUTO-DELETE] Checking if document should be deleted:', {
      windowId,
      exists: !!windowToDelete,
      hasDocState: !!docState,
      entityId: entityId,
      justCreated: docState?.justCreated
    });

    // Check if this is a docs/document window by looking at applicationState
    if (docState && entityId) {
      log.info('[SpaceStore][AUTO-DELETE] Is a Docs application window', { 
        windowId, 
        entityId,
        appType: windowToDelete?.appType 
      });
      
      log.info('[SpaceStore][AUTO-DELETE] Document state:', {
        windowId,
        entityId,
        justCreated: (docState as any)?.justCreated,
        hasContent: (docState as any)?.hasContent,
        wasEdited: (docState as any)?.wasEdited
      });

      // THE CRITICAL CONDITION: Check if document was never edited AND is auto-generated
      // IMPORTANT: Both conditions must be true:
      // 1. justCreated === true (never edited)
      // 2. autoGenerated === true (created via /new route)
      // If either is false, document is NOT deleted
      const isAutoGenerated = (docState as any)?.autoGenerated === true;
      const isNeverEdited = (docState as any)?.justCreated === true;
      const hasContent = (docState as any)?.hasContent === true;
      
      if (isNeverEdited && isAutoGenerated && !hasContent) {
        log.info('[SpaceStore][AUTO-DELETE] ✅ CONDITION MET: Auto-generated document is untouched', { 
          windowId, 
          docId: entityId,
          isAutoGenerated,
          isNeverEdited,
          hasContent
        });

        if (entityId) {
          log.info('[SpaceStore][AUTO-DELETE] Proceeding with auto-deletion', {
            windowId,
            docId: entityId
          });

          // Check if this document is open in any other windows
          const otherWindows = Object.entries(currentState.windows).filter(
            ([id, win]) => {
              const otherEntityId = (win?.applicationState?.docs as any)?.activeDocId || win?.entityId;
              return id !== windowId && otherEntityId === entityId;
            }
          );

          if (otherWindows.length > 0) {
            log.warn('[SpaceStore][AUTO-DELETE] ⚠️ Document is open in other windows, NOT deleting:', {
              docId: entityId,
              otherWindowCount: otherWindows.length,
              otherWindowIds: otherWindows.map(([id]) => id)
            });
          } else {
            log.info('[SpaceStore][AUTO-DELETE] No other windows have this document open, proceeding with deletion', {
              docId: entityId
            });

            try {
              // SAFETY CHECK: Re-verify ALL three conditions before deletion
              // This prevents accidental deletion if any flag was changed between checks
              const currentWindow = get().windows[windowId];
              const currentDocState = currentWindow?.applicationState?.docs as any;
              
              const finalJustCreated = currentDocState?.justCreated === true;
              const finalAutoGenerated = currentDocState?.autoGenerated === true;
              const finalHasContent = currentDocState?.hasContent === true;
              
              log.info('[SpaceStore][AUTO-DELETE] Running final safety checks before deletion:', {
                docId: entityId,
                windowId,
                finalJustCreated,
                finalAutoGenerated,
                finalHasContent
              });
              
              // CRITICAL: All three must pass, or abort deletion
              if (!finalJustCreated || !finalAutoGenerated || finalHasContent) {
                log.warn('[SpaceStore][AUTO-DELETE] ⚠️ SAFETY CHECK FAILED - NOT DELETING:', {
                  docId: entityId,
                  windowId,
                  reason: 'Document was edited, user-created, or has content',
                  justCreated: finalJustCreated,
                  autoGenerated: finalAutoGenerated,
                  hasContent: finalHasContent
                });
                return;
              }
              
              // ✅ USE ENTITY ACTIONS: Handles deletion, CRDT sync, events, undo/redo
              log.info('[SpaceStore][AUTO-DELETE] Calling actions.softDeleteEntity()...', {
                docId: entityId
              });

              useEntityStore.getState().actions.softDeleteEntity(entityId).then(async () => {
                log.info('[SpaceStore][AUTO-DELETE] ✅ Successfully auto-deleted document:', {
                  docId: entityId
                });
                
                // ✅ EMIT DELETION EVENT TO NOTIFY SIDEBAR
                try {
                  const { entityEventService } = await import('../services/entityEventService');
                  
                  // Emit event with basic entity info
                  entityEventService.entityDeleted(
                    {
                      _id: entityId,
                      id: entityId,
                      name: 'Deleted Document',
                      entityType: 'Doc',
                    } as any,
                    spaceId,
                    {
                      command: 'auto-delete-untouched-document'
                    }
                  );
                  
                  log.info('[SpaceStore][AUTO-DELETE] ✅ Deletion event emitted to sidebar:', {
                    docId: entityId,
                    spaceId: spaceId
                  });
                } catch (eventError) {
                  log.error('[SpaceStore][AUTO-DELETE] ⚠️ Failed to emit deletion event (but document was deleted):', {
                    docId: entityId,
                    error: eventError instanceof Error ? eventError.message : String(eventError)
                  });
                }
              }).catch((error: any) => {
                log.error('[SpaceStore][AUTO-DELETE] ❌ Failed to auto-delete document:', {
                  docId: entityId,
                  error: error?.message || error
                });
              });
            } catch (deleteError) {
              log.error('[SpaceStore][AUTO-DELETE] ❌ Error during deletion attempt:', {
                docId: entityId,
                error: deleteError instanceof Error ? deleteError.message : String(deleteError)
              });
            }
          }
        } else {
          log.warn('[SpaceStore][AUTO-DELETE] ⚠️ No document ID found, cannot delete', { windowId });
        }
      } else {
        log.info('[SpaceStore][AUTO-DELETE] ❌ CONDITION NOT MET: Will NOT delete document', {
          windowId,
          entityId,
          justCreated: (docState as any)?.justCreated,
          autoGenerated: (docState as any)?.autoGenerated,
          hasContent: (docState as any)?.hasContent,
          reason: 'Not all deletion conditions were met - document is protected'
        });
      }
    } else {
      log.info('[SpaceStore][AUTO-DELETE] Not a documents window or missing entityId, skipping auto-delete check', {
        windowId,
        hasDocState: !!docState,
        entityId: entityId,
        appType: windowToDelete?.appType
      });
    }
    // ⭐⭐⭐ END AUTO-DELETE LOGIC ⭐⭐⭐

    // Check if we're in the middle of a drag operation for this window
    const isDragging = (window as any).__windowDragging && 
                      (window as any).__lastDragWindowId === windowId;
    
    if (isDragging) {
      log.log('[SpaceStore] Delaying window removal - drag operation in progress');
      // Defer removal until drag completes
      setTimeout(() => get().removeWindow(windowId), 100);
      return;
    }

    // Clear any pending drag operations for this window
    if ((window as any).__lastDragWindowId === windowId) {
      delete (window as any).__lastDragWindowId;
      delete (window as any).__lastDragPosition;
    }

    // Find and remove child windows
    const currentWindows = get().windows;
    const childWindowIds = Object.keys(currentWindows).filter(id => 
      currentWindows[id].parentWindowId === windowId
    );

    // Determine next window to focus ONLY if the currently active window is being removed
    let nextActiveWindowId: string | null = null;
    const wasActiveWindow = currentState.activeWindowId === windowId;
    
    if (wasActiveWindow) {
      // ✅ NEW: Check if this window stored a previousActiveWindowId (for launchers like NewWindow/OpenDialog)
      const windowToRemove = currentWindows[windowId];
      const previousActiveWindowId = windowToRemove?.applicationState?.previousActiveWindowId;
      
      log.log('[SpaceStore] Checking for previousActiveWindowId:', {
        windowId,
        windowTitle: windowToRemove?.title,
        hasPreviousWindowId: !!previousActiveWindowId,
        previousActiveWindowId,
        previousWindowExists: previousActiveWindowId ? !!currentWindows[previousActiveWindowId] : false,
        applicationState: windowToRemove?.applicationState
      });
      
      if (previousActiveWindowId && currentWindows[previousActiveWindowId]) {
        // Restore to the window that was active before opening this launcher
        nextActiveWindowId = previousActiveWindowId;
        log.log('[SpaceStore] ✅ Restoring focus to previous window:', {
          previousWindowId: previousActiveWindowId,
          previousWindowTitle: currentWindows[previousActiveWindowId].title
        });
      } else {
        // Check if active window has already changed (e.g., by WindowEntityTabs focus logic)
        // If so, preserve the new active window instead of overriding it
        const currentActiveWindowId = get().activeWindowId;
        
        if (currentActiveWindowId && currentActiveWindowId !== windowId && currentWindows[currentActiveWindowId]) {
          // Active window was already changed to a valid window - preserve it
          nextActiveWindowId = currentActiveWindowId;
          log.log('[SpaceStore] Active window already changed, preserving focus on:', {
            nextWindowId: nextActiveWindowId,
            nextWindowTitle: currentWindows[currentActiveWindowId].title
          });
        } else {
          // Active window hasn't changed or changed to invalid window - use our fallback logic
          // Get all root windows (no child windows) sorted by creation/update time (most recent first)
          const rootWindows = Object.values(currentWindows)
            .filter(window => window.type === 'window' && !window.isChildWindow && window.id !== windowId && !childWindowIds.includes(window.id))
            .sort((a, b) => {
              const timeA = new Date(a.entity?.updated_at || a.entity?.created_at || 0).getTime();
              const timeB = new Date(b.entity?.updated_at || b.entity?.created_at || 0).getTime();
              return timeB - timeA; // Most recent first
            });
          
          if (rootWindows.length > 0) {
            nextActiveWindowId = rootWindows[0].id;
            log.log('[SpaceStore] Active window being removed - focusing next window:', {
              nextWindowId: nextActiveWindowId,
              nextWindowTitle: rootWindows[0].title,
              availableWindows: rootWindows.map(w => ({ id: w.id, title: w.title }))
            });
          } else {
            log.log('[SpaceStore] Active window being removed - no remaining windows to focus');
          }
        }
      }
    } else {
      log.log('[SpaceStore] Non-active window being removed - preserving current focus on:', currentState.activeWindowId);
    }

    // Delete the window and atomically update activeWindowId in one set() call
    // to avoid firing the contextStore subscription twice.
    get()._applyLocalWindowDelete(windowId, {
      source: 'local',
      syncCRDT: true,
      log: true,
      nextActiveWindowId: wasActiveWindow ? nextActiveWindowId : undefined
    });
    
    childWindowIds.forEach(childId => {
      get()._applyLocalWindowDelete(childId, {
        source: 'local',
        syncCRDT: true,
        log: true
      });
    });
    
    set((state) => ({
      selectedIds: state.selectedIds.filter((id) => id !== windowId && !childWindowIds.includes(id)),
    }));
    
    log.log('[SpaceStore] State update after window removal:', {
      removedWindowId: windowId,
      wasActiveWindow,
      newActiveWindowId: wasActiveWindow ? nextActiveWindowId : currentState.activeWindowId,
      remainingWindows: Object.keys(get().windows).length
    });

    // Cancel any pending debounced operations for this window
    debouncedPersistStore.cancel();

    // Note: CRDT sync is now handled by the helper (_applyLocalWindowDelete)
    
    // Update CRDT with new active window only if the active window was removed and changed
    if (wasActiveWindow) {
      if (nextActiveWindowId) {
        spaceCRDTService.updateActiveWindowId(spaceId, nextActiveWindowId);
        log.log('[SpaceStore] Updated CRDT with new active window:', nextActiveWindowId);
      } else {
        spaceCRDTService.updateActiveWindowId(spaceId, '');
        log.log('[SpaceStore] Cleared active window in CRDT - no remaining windows');
      }
    }
    // If wasActiveWindow is false, we don't update CRDT because the active window hasn't changed
    
    // Ensure immediate backend save to prevent window repopulation
    // Use flush to bypass debouncing and save immediately
    setTimeout(async () => {
      // Double-check the window is still removed before saving
      const currentWindows = get().windows;
      if (currentWindows[windowId]) {
        log.warn(`[SpaceStore] Window ${windowId} reappeared before backend save - skipping save`);
        return;
      }
      
      try {
        log.info(`[SpaceStore] Force saving to backend after removing window ${windowId}`);
        await backendSaveQueue.flush(); // Force immediate save
        log.info(`[SpaceStore] ✅ Backend save completed for window removal`);
      } catch (error) {
        log.error(`[SpaceStore] ❌ Failed to save window removal to backend:`, error);
      }
    }, 300); // Delay to give CRDT more time
  },

  updateWindow: (windowId: string, updates: Partial<AnyWindowEntity>) => {
    // Track if this is a position update during dragging
    const isDragOperation = window.__windowDragging && updates.position;  
    
    const spaceId = get().activeSpace?.id;
    if (!spaceId) return;

    // Check if the update includes an entity with child entities
    const hasChildEntities = updates.entity?.skeleton?.childEntities?.length > 0;

    if (hasChildEntities) {
      const childEntityIds = updates.entity.skeleton.childEntities;
      // Check which child windows don't exist yet
      const currentWindows = get().windows;
      const existingChildWindows = Object.values(currentWindows).filter(w => w.parentWindowId === windowId);
      const existingChildEntityIds = existingChildWindows
        .map(w => w.entityId || w.entity?._id || w.entity?.id)
        .filter(Boolean);
      
      const newChildEntityIds = childEntityIds.filter((entityId: string) => !existingChildEntityIds.includes(entityId));
      
      if (newChildEntityIds.length > 0) {
        
        // Create child windows for new entities using proper actions
        newChildEntityIds.forEach(async (childEntityId: string) => {
          try {
            // ✅ USE ENTITY ACTIONS: Fetch entity data using proper store
            const childEntity = await useEntityStore.getState().actions.getEntity(childEntityId);
            
            // Use actions.createWindow for proper window creation
            const childWindowId = await get().actions.createWindow({
              appType: getAppTypeForEntity(childEntity),
              entity: childEntity,
              position: { x: 0, y: 0 }, // Position doesn't matter for child windows
              size: { width: 400, height: 300 } // Size doesn't matter for child windows
            });
            
            // Update window to mark as child window
            get().actions.updateWindow(childWindowId, {
              isChildWindow: true,
              parentWindowId: windowId
            });
            
            log.info('[SpaceStore] Created child window:', { childWindowId, childEntityId });
          } catch (error) {
            log.error('[SpaceStore] Error creating child window:', error);
          }
        });
      } else {
        log.debug('[SpaceStore] All child windows already exist');
      }
    }

    // ✅ FIX: Check if window exists BEFORE updating
    const currentState = get();
    if (!currentState.windows[windowId]) {
      log.warn('[SpaceStore] ⚠️ Attempted to update non-existent window - rejecting update:', {
        windowId,
        updates: Object.keys(updates),
        stackTrace: new Error().stack?.split('\n').slice(2, 6).join('\n')
      });
      // Early return - don't update state or CRDT
      return;
    }

    // ✅ NEW: Use helper for window update
    get()._applyLocalWindowUpdate(windowId, updates, {
      source: 'local',
      syncCRDT: true,
      log: !isDragOperation // Don't log every drag update
    });
    
    // Handle persistence for non-drag operations immediately
    // For drag operations, we'll handle at drag end to avoid excessive API calls
    if (!isDragOperation) {
      // For non-position updates or manual position changes, save with short delay
      backendSaveQueue.schedule(300);
    } else {
      // For drag operations, use debounced save
      debouncedPersistStore();
    }
  },

  selectWindow: (windowId: string, isMultiSelect: boolean) => {
    set((state) => {
      if (isMultiSelect) {
        // Toggle: if already selected, remove it; otherwise add it
        const isAlreadySelected = state.selectedIds.includes(windowId);
        return {
          selectedIds: isAlreadySelected
            ? state.selectedIds.filter(id => id !== windowId)
            : [...state.selectedIds, windowId]
        };
      } else {
        // Plain click: clear all and select just this one
        return {
          selectedIds: windowId ? [windowId] : []
        };
      }
    });
  },

  groupSelected: () => {
    const { selectedIds } = get();
    log.debug('Store groupSelected called with:', { selectedIds });

    if (selectedIds.length < 2) {
      log.debug('Not enough windows selected to group');
      return;
    }

    const spaceId = get().activeSpace?.id;
    if (spaceId) {
      log.debug('Triggering CRDT groupWindows');
      spaceCRDTService.groupWindows(spaceId, selectedIds);
    }
  },

  ungroup: (windowId) => {
    const window = get().windows[windowId] as WindowEntity;
    if (!window?.tabs) return;

    // Remove tab properties from window
    const updatedWindow = {
      ...window,
      tabs: undefined,
      activeTabId: undefined,
      isParentWindow: undefined
    };

    set(state => ({
      windows: {
        ...state.windows,
        [windowId]: updatedWindow
      }
    }));

    spaceCRDTService.ungroupWindows(windowId);
  },

  setWindows: (windows: AnyWindowEntity[]) => {
    log.debug('Store setWindows called with:', {
      windowCount: windows?.length || 0,
      windows,
    });

    set((state) => {
      const newWindows = (windows || []).reduce((acc, win) => {
        // All windows are now just WindowEntity
        if (win && win.id && !acc[win.id]) {
          acc[win.id] = win;
        }
        return acc;
      }, {} as Record<string, AnyWindowEntity>);

      return { windows: newWindows };
    });
  },

  setStatus: (status) => {
    set({ status });
  },

  updateSpaceName: async (newName: string) => {
    const currentSpace = get().activeSpace;
    log.log('[SpaceStore] updateSpaceName called:', {
      newName,
      currentName: currentSpace?.name,
      spaceId: currentSpace?.id,
      hasActiveSpace: !!currentSpace
    });
    
    const spaceId = currentSpace?.id;
    if (!spaceId) {
      const errorMsg = '[SpaceStore] Cannot update name - no active space';
      log.error(errorMsg);
      throw new Error(errorMsg);
    }

    try {
      log.log('[SpaceStore] Calling spaceService.updateSpaceName...');
      await spaceService.updateSpaceName(spaceId, newName);
      log.log('[SpaceStore] spaceService.updateSpaceName succeeded');
      
      // Update local state
      set((state) => ({
        activeSpace: state.activeSpace ? {
          ...state.activeSpace,
          name: newName,
          updatedAt: new Date().toISOString()
        } : null
      }));
      
      log.log('[SpaceStore] Local state updated with new name:', newName);
      
      // ✅ CRITICAL: Update entityStore (Home reads from here!)
      const { default: useEntityStore } = await import('./entityStore');
      const entityStore = useEntityStore.getState();
      const spaceEntity = entityStore.entities.find(e => e.entityType === 'Space' && e._id === spaceId);
      
      if (spaceEntity) {
        entityStore.actions.updateEntity(spaceId, {
          name: newName,
          updated_at: new Date().toISOString()
        });
        log.log('[SpaceStore] ✅ Updated entityStore with new name (Home will see this)');
      } else {
        log.warn('[SpaceStore] ⚠️ Space entity not found in entityStore');
      }
      
      // ✅ PROPER ORCHESTRATION: Update systemStore cache to keep UI in sync
      const useSystemStore = (await import('./systemStore')).default;
      const updatedSpace = {
        ...currentSpace,
        name: newName,
        updatedAt: new Date().toISOString()
      };
      useSystemStore.getState()._applyLocalSpaceUpdate(updatedSpace, {
        source: 'local',
        log: false
      });
      log.log('[SpaceStore] ✅ Updated systemStore cache with new name');
    } catch (error) {
      log.error('[SpaceStore] Failed to update space name:', error);
      throw error;
    }
  },

  updateSpaceChatSettings: async (spaceChatUpdates: Partial<NonNullable<SpaceSettings['spaceChat']>>) => {
    // log.log(formatJSON(spaceChatUpdates, '[SpaceStore] Updating SpaceChat settings'));
    const spaceId = get().activeSpace?.id;
    const activeSpace = get().activeSpace;
    if (!spaceId || !activeSpace) {
      log.error('Cannot update SpaceChat settings - no active space');
      return;
    }

    try {
      // Merge with existing spaceChat settings
      const currentSpaceChatSettings = activeSpace.settings.spaceChat || {
        currentThreadIds: {},
        isHistoryOpen: false,
        mode: 'chat',
        model: await modelService.getUserPreferredModel(),
        isContextVisible: false,
        widths: { focused: 450, spatial: 500 }
      };

      const newSpaceChatSettings = { ...currentSpaceChatSettings, ...spaceChatUpdates };
      const newSettings = { ...activeSpace.settings, spaceChat: newSpaceChatSettings };

      // Update via spaceService (which handles backend persistence)
      await spaceService.updateSpace(spaceId, activeSpace.windows, newSettings, get().activeWindowId, get().contextEntities, get().contextExcerpts);
      
      // Update local state after successful backend update
      set((state) => ({
        activeSpace: state.activeSpace ? {
          ...state.activeSpace,
          settings: newSettings
        } : null
      }));

      // Also update CRDT for real-time collaboration
      spaceCRDTService.updateSettings(spaceId, newSettings);
      
      // ✅ PROPER ORCHESTRATION: Update systemStore cache to keep UI in sync
      const useSystemStore = (await import('./systemStore')).default;
      const updatedSpace = {
        ...activeSpace,
        settings: newSettings,
        updatedAt: new Date().toISOString()
      };
      useSystemStore.getState()._applyLocalSpaceUpdate(updatedSpace, {
        source: 'local',
        log: false
      });
    } catch (error) {
      log.error('Failed to update SpaceChat settings:', error);
      throw error;
    }
  },

  updateEntitySidebarSettings: async (sidebarSettings: Partial<NonNullable<SpaceSettings['entitySidebar']>>) => {
    const spaceId = get().activeSpace?.id;
    const activeSpace = get().activeSpace;
    if (!spaceId || !activeSpace) {
      log.error('Cannot update EntitySidebar settings - no active space');
      return;
    }

    try {
      // Merge with existing entitySidebar settings
      const currentSidebarSettings = activeSpace.settings.entitySidebar || {
        expandedFolderIds: [],
        currentFolderId: null,
        folderPath: []
      };

      const newSidebarSettings = { ...currentSidebarSettings, ...sidebarSettings };
      const newSettings = { ...activeSpace.settings, entitySidebar: newSidebarSettings };

      // Update via spaceService (which handles backend persistence)
      await spaceService.updateSpace(spaceId, activeSpace.windows, newSettings, get().activeWindowId, get().contextEntities, get().contextExcerpts);
      
      // Update local state after successful backend update
      set((state) => ({
        activeSpace: state.activeSpace ? {
          ...state.activeSpace,
          settings: newSettings
        } : null
      }));

      // Also update CRDT for real-time collaboration
      spaceCRDTService.updateSettings(spaceId, newSettings);
      
      // ✅ PROPER ORCHESTRATION: Update systemStore cache to keep UI in sync
      const useSystemStore = (await import('./systemStore')).default;
      const updatedSpace = {
        ...activeSpace,
        settings: newSettings,
        updatedAt: new Date().toISOString()
      };
      useSystemStore.getState()._applyLocalSpaceUpdate(updatedSpace, {
        source: 'local',
        log: false
      });
    } catch (error) {
      log.error('Failed to update EntitySidebar settings:', error);
      throw error;
    }
  },

  cleanup: () => {
    const { cleanupFn } = get();
    log.log('[SpaceStore] Cleaning up...');
    if (cleanupFn) {
      cleanupFn();
    }
    
    useSpatialUIStore.getState().reset();
    
    set({ 
      status: 'idle',
      activeSpace: null,
      windows: {},
      entities: {},
      contextEntities: {}, // ✅ NEW: Reset context on cleanup
      initialized: false,
      cleanupFn: () => {}
    } as Partial<SpaceStore>);
  },

  setBackground: async (background: SpaceBackground) => {
    const spaceId = get().activeSpace?.id;
    if (!spaceId) return;

    const activeSpace = get().activeSpace;
    if (!activeSpace) return;

    const newSettings = {
      ...activeSpace.settings,
      background
    };

    // Update local state immediately for UI responsiveness
    set((state) => ({
      activeSpace: state.activeSpace ? {
        ...state.activeSpace,
        settings: newSettings
      } : null
    }));

    try {
      // Persist to backend (include activeWindowId)
      await spaceService.updateSpace(spaceId, activeSpace.windows, newSettings, get().activeWindowId, get().contextEntities, get().contextExcerpts);
      log.log('[SpaceStore] Successfully updated background in backend');
      
      // Also update CRDT for real-time collaboration
      spaceCRDTService.updateSettings(spaceId, newSettings);
      
      // ✅ PROPER ORCHESTRATION: Update systemStore cache to keep UI in sync
      const useSystemStore = (await import('./systemStore')).default;
      const updatedSpace = {
        ...activeSpace,
        settings: newSettings,
        updatedAt: new Date().toISOString()
      };
      useSystemStore.getState()._applyLocalSpaceUpdate(updatedSpace, {
        source: 'local',
        log: false
      });
      log.log('[SpaceStore] ✅ Updated systemStore cache with new background');
    } catch (error) {
      log.error('[SpaceStore] Error updating background in backend:', error);
    }
  },

  toggleDebugger: () => set(state => ({ showDebugger: !state.showDebugger })),

  setActiveWindow: (windowId: string | null) => {
    log.log('[SpaceStore] Setting active window:', windowId);
    set({ activeWindowId: windowId });
    
    // ✅ FIXED: Store activeWindowId in localStorage (per-tab), not CRDT/backend
    const spaceId = get().activeSpace?.id;
    if (spaceId && windowId) {
      // Store in localStorage for per-tab preference
      try {
        localStorage.setItem(`activeWindowId_${spaceId}`, windowId);
        log.log('[SpaceStore] Saved activeWindowId to localStorage for space:', spaceId);
      } catch (error) {
        console.error('[SpaceStore] Failed to save activeWindowId to localStorage:', error);
      }
      
      // ✅ NEW: Also persist to backend for cross-session restoration
      backendSaveQueue.schedule(500);
    }
    
    // 🚨 CRITICAL: Trigger context update when active window changes
    setTimeout(() => {
      try {
        // Dispatch custom event to notify SpaceChat of active window change
        const event = new CustomEvent('active-window-changed', {
          detail: { 
            windowId, 
            spaceId: get().activeSpace?.id,
            reason: 'setActiveWindow_called'
          }
        });
        window.dispatchEvent(event);
        
        log.log('🔄 [ACTIVE_WINDOW_CHANGE] Dispatched context update event:', {
          windowId,
          reason: 'active_window_changed'
        });
      } catch (error) {
        console.error('❌ [ACTIVE_WINDOW_CHANGE] Failed to dispatch context update event:', error);
      }
    }, 100); // Small delay to ensure window state is updated
    
    // Close context menu when active window changes
    if (spaceId) {
      (window as any).closeContextMenu?.();
    }
  },

  bringWindowToFront: (windowId: string) => {
    const { windows, initializationStatus, activeWindowId: currentActiveWindowId } = get();
    if (!windows[windowId]) {
      log.warn('[SpaceStore] Attempted to bring non-existent window to front:', windowId);
      return;
    }
    
    // ✅ PERFORMANCE FIX: Skip if already active (prevents redundant state update)
    if (currentActiveWindowId === windowId) {
      log.log('[SpaceStore] bringWindowToFront skipped - window already active:', windowId);
      return;
    }

    // ✅ DEBUG: Log when activeWindowId changes during initialization
    if (initializationStatus === 'initialized' && currentActiveWindowId) {
      console.warn('[SpaceStore] 🔄 ActiveWindowId being changed after initialization:', {
        from: currentActiveWindowId,
        to: windowId,
        fromTitle: windows[currentActiveWindowId]?.title,
        toTitle: windows[windowId]?.title,
        stackTrace: new Error().stack?.split('\n').slice(1, 5)
      });
    }

    log.log('[SpaceStore] bringWindowToFront setting activeWindowId to:', windowId);
    
    set({ 
      activeWindowId: windowId
    });
    
    const spaceId = get().activeSpace?.id;
    if (spaceId) {
      // Store in localStorage for per-tab preference
      try {
        localStorage.setItem(`activeWindowId_${spaceId}`, windowId);
      } catch (error) {
        console.error('[SpaceStore] Failed to save activeWindowId to localStorage:', error);
      }
      
      // ✅ NEW: Also persist to backend for cross-session restoration
      backendSaveQueue.schedule(500);
      
      // Close context menu when active window changes
      (window as any).closeContextMenu?.();
    }
  },

  setViewMode: async (viewMode: 'spatial' | 'focused' | 'gui2' | 'home' | 'canvas') => {
    const spaceId = get().activeSpace?.id;
    if (!spaceId) return;

    const activeSpace = get().activeSpace;
    if (!activeSpace) return;

    const newSettings = {
      ...activeSpace.settings,
      viewMode
    };

    // Update local state immediately for UI responsiveness
    set((state) => ({
      activeSpace: state.activeSpace ? {
        ...state.activeSpace,
        settings: newSettings
      } : null
    }));

    try {
      // Persist to backend
      await spaceService.updateSpace(spaceId, activeSpace.windows, newSettings, get().activeWindowId, get().contextEntities);
      log.log('[SpaceStore] Successfully updated viewMode in backend');
      
      // Also update CRDT for real-time collaboration
      spaceCRDTService.updateSettings(spaceId, newSettings);
      
      // ✅ PROPER ORCHESTRATION: Update systemStore cache to keep UI in sync
      const useSystemStore = (await import('./systemStore')).default;
      const updatedSpace = {
        ...activeSpace,
        settings: newSettings,
        updatedAt: new Date().toISOString()
      };
      useSystemStore.getState()._applyLocalSpaceUpdate(updatedSpace, {
        source: 'local',
        log: false
      });
      log.log('[SpaceStore] ✅ Updated systemStore cache with new viewMode');
    } catch (error) {
      log.error('[SpaceStore] Error updating viewMode in backend:', error);
    }
  },

  saveToBackend: async () => {
    const spaceId = get().activeSpace?.id;
    if (!spaceId) return;

    const state = get();
    const windows = state.windows;
    const settings = state.activeSpace?.settings;
    const activeWindowId = state.activeWindowId;
    
    log.log('[SpaceStore] Persisting to backend:', {
      spaceId,
      windows: Object.keys(windows).length,
      activeWindowId,
      note: 'Now persisting activeWindowId to backend'
    });
    
    log.log('[SpaceStore] Persisting to backend after space update');
    
    try {
      await spaceService.updateSpace(spaceId, windows, settings || {}, activeWindowId, state.contextEntities, state.contextExcerpts);
      log.log('[SpaceStore] ✅ Successfully persisted space to backend (including activeWindowId)');
    } catch (error) {
      log.error('[SpaceStore] ❌ CRITICAL: Failed to persist space to backend!', {
        error: error instanceof Error ? error.message : String(error),
        spaceId,
        windowCount: Object.keys(windows).length,
        contextEntitiesCount: Object.keys(state.contextEntities || {}).length
      });
      // Re-throw so callers know the save failed
      throw error;
    }
  },

  // Entity access
  refreshWindowEntityFromBackend: async (windowId: string) => {
    const state = get();
    const window = state.windows[windowId];
    if (!window?.entityId) {
      log.warn('[SpaceStore] Cannot refresh window entity - no entityId:', windowId);
      return;
    }
    
    try {
      log.info('[SpaceStore] Refreshing window entity from backend:', { windowId, entityId: window.entityId });
      // ✅ USE ENTITY ACTIONS: Fetch entity data using proper store
      const freshEntity = await useEntityStore.getState().actions.getEntity(window.entityId);
      
      if (freshEntity) {
        log.info('[SpaceStore] ✅ Refreshed entity from backend:', { 
          entityId: freshEntity._id, 
          name: freshEntity.name,
          hasLlmId: !!freshEntity.llmId,
          llmId: freshEntity.llmId 
        });
        
        // ✅ REFACTOR: No need to store in spaceStore - entityStore is single source of truth
        // Entity is already in entityStore from the getEntity call
      } else {
        log.warn('[SpaceStore] No entity returned from backend:', window.entityId);
      }
    } catch (error) {
      log.error('[SpaceStore] Failed to refresh window entity:', error);
    }
  },
  
  getWorkspaceEntities: () => {
    const state = get();
    const workspaceEntities: Array<{ llmId: string; name: string; type: string; windowId: string; entity: any }> = [];
    
    log.log('[SpaceStore] Getting workspace entities from windows:', {
      totalWindows: Object.keys(state.windows).length,
      windowIds: Object.keys(state.windows)
    });
    
    // ✅ REFACTOR: Use entityStore as single source of truth
    const entityStoreState = useEntityStore.getState();
    
    for (const [windowId, windowEntity] of Object.entries(state.windows)) {
      const entityLlmId = windowEntity.entityLlmId;
      const entity = entityLlmId ? entityStoreState.entities.find((e: any) => e.llmId === entityLlmId) : null;
      
      if (entity && entity.llmId) {
        workspaceEntities.push({
          llmId: entity.llmId,
          name: entity.name || windowEntity.title || 'Untitled',
          type: entity.entityType || 'Doc',
          windowId,
          entity
        });
        log.log('[SpaceStore] ✅ Added entity with llmId:', entity.llmId);
      } else if (entityLlmId) {
        log.warn('[SpaceStore] Skipping window with missing entity:', { 
          entityLlmId, 
          windowId
        });
      }
    }
    
    log.log('[SpaceStore] Found workspace entities:', workspaceEntities.length, workspaceEntities.map(e => `${e.name}(${e.llmId})`));
    return workspaceEntities;
  },

  // Sidebar actions
  setSidebarVisible: async (isVisible: boolean) => {
    // Update local state immediately for UI responsiveness
    set({ isSidebarVisible: isVisible });
    
    // Persist to backend
    const state = get();
    const spaceId = state.activeSpace?.id;
    const activeSpace = state.activeSpace;
    
    if (spaceId && activeSpace) {
      const newSidebarSettings = {
        isVisible,
        width: state.sidebarWidth,
        mode: state.sidebarMode,
      };
      
      const newSettings = {
        ...activeSpace.settings,
        sidebar: newSidebarSettings
      };
      
      try {
        // Update backend first (include activeWindowId)
        await spaceService.updateSpace(spaceId, activeSpace.windows, newSettings, state.activeWindowId, state.contextEntities, state.contextExcerpts);
        log.log('[SpaceStore] Successfully updated sidebar visibility in backend');
        
        // Update local activeSpace to keep in sync
        set((state) => ({
          activeSpace: state.activeSpace ? {
            ...state.activeSpace,
            settings: newSettings
          } : null
        }));
        
        // Also update CRDT for real-time collaboration
        spaceCRDTService.updateSettings(spaceId, newSettings);
        
        // ✅ PROPER ORCHESTRATION: Update systemStore cache to keep UI in sync
        const useSystemStore = (await import('./systemStore')).default;
        const updatedSpace = {
          ...activeSpace,
          settings: newSettings,
          updatedAt: new Date().toISOString()
        };
        useSystemStore.getState()._applyLocalSpaceUpdate(updatedSpace, {
          source: 'local',
          log: false
        });
      } catch (error) {
        log.error('[SpaceStore] Error updating sidebar visibility in backend:', error);
      }
    }
  },

  setSidebarWidth: async (width: number) => {
    // Update local state immediately for UI responsiveness
    // Also mirror the width into activeSpace.settings.sidebar so scheduled saves persist the latest value
    set((state) => {
      if (!state.activeSpace) {
        return { sidebarWidth: width } as Partial<SpaceStore>;
      }
      const newSidebarSettings = {
        isVisible: state.isSidebarVisible,
        width,
        mode: state.sidebarMode,
      };
      const newSettings = {
        ...state.activeSpace.settings,
        sidebar: newSidebarSettings,
      };
      return {
        sidebarWidth: width,
        activeSpace: {
          ...state.activeSpace,
          settings: newSettings,
        },
      } as Partial<SpaceStore>;
    });

    // Debounce backend persistence to avoid hammering the API during drag
    backendSaveQueue.schedule(300);
  },

  setSidebarMode: async (mode: 'normal' | 'overlay') => {
    // Update local state immediately for UI responsiveness
    set({ sidebarMode: mode });
    
    // Persist to backend
    const state = get();
    const spaceId = state.activeSpace?.id;
    const activeSpace = state.activeSpace;
    
    if (spaceId && activeSpace) {
      const newSidebarSettings = {
        isVisible: state.isSidebarVisible,
        width: state.sidebarWidth,
        mode,
      };
      
      const newSettings = {
        ...activeSpace.settings,
        sidebar: newSidebarSettings
      };
      
      try {
        // Update backend first
        await spaceService.updateSpace(spaceId, activeSpace.windows, newSettings, state.activeWindowId, state.contextEntities);
        log.log('[SpaceStore] Successfully updated sidebar mode in backend');
        
        // Update local activeSpace to keep in sync
        set((state) => ({
          activeSpace: state.activeSpace ? {
            ...state.activeSpace,
            settings: newSettings
          } : null
        }));
        
        // Also update CRDT for real-time collaboration
        spaceCRDTService.updateSettings(spaceId, newSettings);
        
        // ✅ PROPER ORCHESTRATION: Update systemStore cache to keep UI in sync
        const useSystemStore = (await import('./systemStore')).default;
        const updatedSpace = {
          ...activeSpace,
          settings: newSettings,
          updatedAt: new Date().toISOString()
        };
        useSystemStore.getState()._applyLocalSpaceUpdate(updatedSpace, {
          source: 'local',
          log: false
        });
      } catch (error) {
        log.error('[SpaceStore] Error updating sidebar mode in backend:', error);
      }
    }
  },

  setChatWidth: async (width: number) => {
    // Update local state immediately for UI responsiveness
    // Also mirror the width into activeSpace.settings.spaceChat so scheduled saves persist the latest value
    set((state) => {
      if (!state.activeSpace) {
        return {} as Partial<SpaceStore>;
      }
      const currentSpaceChatSettings = state.activeSpace.settings.spaceChat || {
        currentThreadIds: {},
        isHistoryOpen: false,
        mode: 'chat',
        model: modelService.getDefaultModel(),
        isContextVisible: false,
        widths: { focused: 320, spatial: 320 }
      };
      
      const newSpaceChatSettings = {
        ...currentSpaceChatSettings,
        widths: {
          ...currentSpaceChatSettings.widths,
          focused: width // Update the focused layout width
        }
      };
      
      const newSettings = {
        ...state.activeSpace.settings,
        spaceChat: newSpaceChatSettings,
      };
      
      return {
        activeSpace: {
          ...state.activeSpace,
          settings: newSettings,
        },
      } as Partial<SpaceStore>;
    });

    // Debounce backend persistence to avoid hammering the API during drag
    backendSaveQueue.schedule(300);
  },

  toggleChatExpanded: async () => {
    const state = get();
    const newExpandedState = !state.isChatExpanded;
    
    console.log('[SpaceStore] 🔍 toggleChatExpanded called:', {
      from: state.isChatExpanded,
      to: newExpandedState,
      spaceId: state.activeSpace?.id
    });
    
    if (newExpandedState) {
      // EXPANDING: Save current state before hiding everything
      const chatWidth = state.activeSpace?.settings?.spaceChat?.widths?.focused || 420;
      
      set({
        previousLayoutState: {
          sidebarVisible: state.isSidebarVisible,
          sidebarWidth: state.sidebarWidth,
          chatWidth: chatWidth,
        },
        isChatExpanded: true,
        isSidebarVisible: false // Hide sidebar using existing mechanism
      });
      
      console.log('[SpaceStore] ✅ Chat expanded, previous state saved:', get().previousLayoutState);
    } else {
      // COLLAPSING: Restore previous state
      const prev = state.previousLayoutState;
      if (prev) {
        console.log('[SpaceStore] 🔄 Restoring previous state:', prev);
        
        set({
          isChatExpanded: false,
          isSidebarVisible: prev.sidebarVisible,
          sidebarWidth: prev.sidebarWidth,
          previousLayoutState: null
        });
        
        // Restore chat width
        await get().setChatWidth(prev.chatWidth);
        
        console.log('[SpaceStore] ✅ Previous state restored');
      } else {
        // No previous state, just collapse
        set({ isChatExpanded: false });
      }
    }
    
    // Persist to backend (debounced)
    backendSaveQueue.schedule(300);
  },

  // Hierarchical operations
  moveWindowToGroup: async (windowId: string, targetGroupId: string | null, position: 'above' | 'below' | 'inside') => {
    const state = get();
    const spaceId = state.activeSpace?.id;
    if (!spaceId) return;

    const sourceWindow = state.windows[windowId];
    if (!sourceWindow) {
      log.error(`[SpaceStore] Cannot move window ${windowId} - not found`);
      return;
    }

    log.log(`[SpaceStore] Moving window ${windowId} to group ${targetGroupId} at position ${position}`);

    try {
      if (position === 'inside' && targetGroupId) {
        // Move window inside a group
        const targetWindow = state.windows[targetGroupId];
        if (targetWindow?.entity?.entityType === 'Group') {
          const currentChildEntities = targetWindow.entity.skeleton?.childEntities || [];
          
          // Add the source entity to the group's children
          const newChildEntities = [...currentChildEntities, sourceWindow.entity?._id || sourceWindow.entity?.id].filter(Boolean);
          
          // Update the group entity via entityService
          const updatedGroupEntity = {
            ...targetWindow.entity,
            skeleton: {
              ...targetWindow.entity.skeleton,
              childEntities: newChildEntities
            }
          };

          // Update backend
          await entityService.update(targetWindow.entity._id, updatedGroupEntity);
          
          // Update local state
          set((state) => ({
            windows: {
              ...state.windows,
              [targetGroupId]: {
                ...state.windows[targetGroupId],
                entity: updatedGroupEntity
              }
            }
          }));

          // Update CRDT
          spaceCRDTService.updateWindow(spaceId, targetGroupId, { entity: updatedGroupEntity });
          
          log.log(`[SpaceStore] Successfully moved window ${windowId} into group ${targetGroupId}`);
        }
      } else {
        // Move window above/below another window (reordering)
        log.log(`[SpaceStore] Reordering window ${windowId} ${position} ${targetGroupId || 'root'}`);
        
        // For now, just trigger a backend save to persist current state
        await get().saveToBackend();
      }
    } catch (error) {
      log.error(`[SpaceStore] Error moving window ${windowId}:`, error);
    }
  },

  createGroupFromWindows: async (windowIds: string[], groupName?: string) => {
    const state = get();
    const spaceId = state.activeSpace?.id;
    if (!spaceId || windowIds.length < 2) {
      log.error('[SpaceStore] Cannot create group - need at least 2 windows and valid space');
      return null;
    }

    const finalGroupName = groupName || `New Group ${Date.now()}`;
    log.log(`[SpaceStore] Creating group "${finalGroupName}" from windows:`, windowIds);

    try {
      // Get entities from the selected windows
      const entityIds = windowIds
        .map(windowId => state.windows[windowId]?.entity?._id || state.windows[windowId]?.entity?.id)
        .filter(Boolean);

      if (entityIds.length === 0) {
        log.error('[SpaceStore] No valid entities found in selected windows');
        return null;
      }

      // Create group entity
      const groupEntity = {
        entityType: 'Group',
        name: finalGroupName,
        skeleton: {
          '@type': 'Group',
          groupName: finalGroupName,
          childEntities: entityIds,
          isCollapsed: false, // Start expanded so users can see their grouped items
        },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // ✅ USE ENTITY ACTIONS: Handles creation, CRDT sync, events, undo/redo
      const createdGroup = await useEntityStore.getState().actions.createEntity(groupEntity);
      log.log('[SpaceStore] Group created successfully:', createdGroup);
      log.log('[SpaceStore] Group response keys:', Object.keys(createdGroup));
      log.log('[SpaceStore] Group response id field:', createdGroup.id);
      log.log('[SpaceStore] Group response _id field:', createdGroup._id);

      // Get the group ID from the response (could be 'id' or '_id')
      const groupId = createdGroup.id || createdGroup._id;
      log.log('[SpaceStore] Extracted group ID:', groupId);
      if (!groupId) {
        log.error('[SpaceStore] No group ID found in response:', createdGroup);
        return null;
      }

      // Create a virtual window for the group - this represents the group in the hierarchy
      // but doesn't open as a separate app
      const groupWindow: WindowEntity = {
        id: `group-${groupId}`,
        type: 'window',
        appType: 'group', // Use special 'group' appType to indicate this is a container
        title: finalGroupName,
        entity: {
          ...createdGroup,
          _id: groupId,
          id: groupId,
          entityType: 'Group', // Ensure entityType is set correctly
          name: finalGroupName, // Ensure name is set
          skeleton: {
            '@type': 'Group',
            groupName: finalGroupName,
            childEntities: entityIds, // Include the child entity IDs
            isCollapsed: false // Start expanded so users can see their grouped items
          }
        },
        position: { x: 100, y: 100 },
        size: { width: 400, height: 300 },
      };

      // Add the group window to the space
      get().addWindow(groupWindow);

      // DON'T remove the original windows - they should remain as children of the group
      // The hierarchical document list will handle showing them as children

      log.log(`[SpaceStore] Successfully created group "${finalGroupName}" with ID: ${groupId}`);
      return groupId;
    } catch (error) {
      log.error('[SpaceStore] Error creating group:', error);
      return null;
    }
  },

  ungroupWindows: async (groupId: string) => {
    const state = get();
    const spaceId = state.activeSpace?.id;
    if (!spaceId) return false;

    const groupWindow = state.windows[groupId];
    if (!groupWindow?.entity || groupWindow.entity.entityType !== 'Group') {
      log.error(`[SpaceStore] Cannot ungroup - ${groupId} is not a valid group`);
      return false;
    }

    log.log(`[SpaceStore] Ungrouping group ${groupId}`);

    try {
      const childEntityIds = groupWindow.entity.skeleton?.childEntities || [];
      
      // Create windows for each child entity
      const childWindows: WindowEntity[] = [];
      for (const entityId of childEntityIds) {
        try {
          // ✅ USE ENTITY ACTIONS: Fetch entity data using proper store
          const entity = await useEntityStore.getState().actions.getEntity(entityId);
          
          // ✅ REFACTOR: No need to store in spaceStore - entityStore is single source of truth
          
          const childWindow: NormalizedWindowEntity = {
             id: `window-${entity._id}`,
             type: 'window',
             appType: entity.entityType === 'File' ? 'docs' : 'entityBrowser',
             title: entity.name,
             // ✅ FIXED: Store references instead of embedded entity
             entityId: entity._id || entity.id,
             entityLlmId: entity.llmId,
             position: { 
               x: groupWindow.position.x + Math.random() * 100, 
               y: groupWindow.position.y + Math.random() * 100 
             },
             size: { width: 400, height: 300 },
           };

          childWindows.push(childWindow);
        } catch (error) {
          log.error(`[SpaceStore] Error fetching entity ${entityId} for ungrouping:`, error);
        }
      }

      // Add child windows to the space
      childWindows.forEach(window => {
        get().addWindow(window);
      });

      // ✅ USE ENTITY ACTIONS: Handles deletion, CRDT sync, events, undo/redo
      await useEntityStore.getState().actions.softDeleteEntity(groupWindow.entity._id);

      // Remove the group window
      get().removeWindow(groupId);

      log.log(`[SpaceStore] Successfully ungrouped ${childWindows.length} windows from group ${groupId}`);
      return true;
    } catch (error) {
      log.error(`[SpaceStore] Error ungrouping ${groupId}:`, error);
      return false;
    }
  },

  updateWindowOrder: async (windowIds: string[], parentGroupId?: string | null) => {
    const state = get();
    const spaceId = state.activeSpace?.id;
    if (!spaceId) return;

    log.log(`[SpaceStore] Updating window order for ${windowIds.length} windows in ${parentGroupId || 'root'}`);

    try {
      if (parentGroupId) {
        // Update order within a group
        const groupWindow = state.windows[parentGroupId];
        if (groupWindow?.entity?.entityType === 'Group') {
          const entityIds = windowIds
            .map(windowId => state.windows[windowId]?.entity?._id || state.windows[windowId]?.entity?.id)
            .filter(Boolean);

          const updatedGroupEntity = {
            ...groupWindow.entity,
            skeleton: {
              ...groupWindow.entity.skeleton,
              childEntities: entityIds
            }
          };

          // Update backend
          await entityService.update(groupWindow.entity._id, updatedGroupEntity);
          
          // Update local state
          set((state) => ({
            windows: {
              ...state.windows,
              [parentGroupId]: {
                ...state.windows[parentGroupId],
                entity: updatedGroupEntity
              }
            }
          }));

          // Update CRDT
          spaceCRDTService.updateWindow(spaceId, parentGroupId, { entity: updatedGroupEntity });
        }
      } else {
        // Update root-level order
        // For now, just save current state to backend
        await get().saveToBackend();
      }

      log.log(`[SpaceStore] Successfully updated window order`);
    } catch (error) {
      log.error(`[SpaceStore] Error updating window order:`, error);
    }
  },

  updateGroupCollapsedState: async (groupId: string, isCollapsed: boolean) => {
    const state = get();
    const spaceId = state.activeSpace?.id;
    if (!spaceId) return;

    const groupWindow = state.windows[groupId];
    if (!groupWindow?.entity || groupWindow.entity.entityType !== 'Group') {
      log.error(`[SpaceStore] Cannot update collapsed state - ${groupId} is not a valid group`);
      return;
    }

    log.log(`[SpaceStore] Updating group ${groupId} collapsed state to ${isCollapsed}`);

    try {
      // Update the group entity with the new collapsed state
      const updatedGroupEntity = {
        ...groupWindow.entity,
        skeleton: {
          ...groupWindow.entity.skeleton,
          isCollapsed
        }
      };

      // Update backend
      await entityService.update(groupWindow.entity._id, updatedGroupEntity);
      
      // Update local state
      set((state) => ({
        windows: {
          ...state.windows,
          [groupId]: {
            ...state.windows[groupId],
            entity: updatedGroupEntity
          }
        }
      }));

      // Update CRDT
      spaceCRDTService.updateWindow(spaceId, groupId, { entity: updatedGroupEntity });
      
      log.log(`[SpaceStore] Successfully updated group ${groupId} collapsed state`);
    } catch (error) {
      log.error(`[SpaceStore] Error updating group collapsed state:`, error);
    }
  },

  // ✅ SURGICAL FIX: New normalized entity access methods
  getWindowEntity: (windowId: string) => {
    const state = get();
    const window = state.windows[windowId];
    if (!window) return null;

    // Short-circuit for "New Window" placeholder slots — they use a Window-typed entity
    // as a placeholder that is never meaningful for context or entity lookup.
    // Skipping the entity store search + REST fallback avoids a 553ms blocking fetch on load.
    if ((window.applicationState as any)?.isNew === true) {
      return null;
    }

    // ✅ FIX: If window has entity stored directly, return it immediately
    // This is critical for custom apps where we fetch the App entity (with source code)
    // and store it directly on the window, but it might not be in entityStore yet
    if (window.entity) {
      log.log('[getWindowEntity] ✅ Returning direct entity reference from window:', {
        windowId,
        entityId: window.entity._id || window.entity.id,
        entityType: window.entity.entityType,
        hasSourceFiles: !!(window.entity.skeleton?.source?.files)
      });
      return window.entity;
    }
    
    // ✅ REFACTOR: Always use entityStore as single source of truth
    const entities = useEntityStore.getState().entities;
    
    // Get applicationState entityId early (needed for multiple checks)
    const appStateEntityId = getWindowEntityId(window);
    
    // PERFORMANCE: Some windows don't have entities (EntityBrowser, etc.) - this is normal
    // Only look up entities for windows that should have them
    if (!window.entityId && !window.entityLlmId && !appStateEntityId) {
      return null; // Silently return null for windows without entity references
    }

    // If entityStore hasn't finished loading yet, the entity simply isn't available yet.
    // Return null silently — the caller will re-evaluate once entityStore initializes.
    // This check must come BEFORE the entity lookup so we don't log ❌ errors for
    // a perfectly normal transient state that resolves on its own.
    if (!useEntityStore.getState().initialized) {
      log.log('[getWindowEntity] entity store not yet initialized, deferring lookup', { windowId, appStateEntityId });
      return null;
    }

    // Find entity by entityId first (most precise), then llmId (which can have collisions)
    // ⚠️ CRITICAL: entityId (_id) is unique and reliable
    // ⚠️ WARNING: llmId can have collisions due to generator bugs!
    let entity = null;
    
    // Try entityId first (MOST RELIABLE - unique database ID)
    if (window.entityId) {
      entity = entities.find((e: any) => e._id === window.entityId || e.id === window.entityId);
      if (entity) {
        return entity;
      }
    }
    
    // 🔧 FIX: Try applicationState entityId (activeDocId, entityId, etc.)
    // This handles cases where window.entityId is not set but applicationState has it
    if (appStateEntityId) {
      entity = entities.find((e: any) => e._id === appStateEntityId || e.id === appStateEntityId);
      if (entity) {
        // ⚠️ VALIDATION: If window.entityLlmId is also set, verify they match
        if (window.entityLlmId && entity.llmId !== window.entityLlmId) {
          console.warn('[getWindowEntity] ⚠️ Mismatch detected - window.entityLlmId does not match entity found via applicationState!', {
            windowId,
            windowEntityLlmId: window.entityLlmId,
            appStateEntityId,
            foundEntityLlmId: entity.llmId,
            foundEntityName: entity.name,
            note: 'Using entity from applicationState (more reliable)'
          });
        }
        return entity;
      }
    }
    
    // Fall back to llmId (WARNING: can have collisions!)
    if (window.entityLlmId) {
      const matchingEntities = entities.filter((e: any) => e.llmId === window.entityLlmId);
      
      if (matchingEntities.length > 1) {
        console.error('[getWindowEntity] 🚨 llmId COLLISION detected! Cannot reliably determine entity!', {
          llmId: window.entityLlmId,
          collisionCount: matchingEntities.length,
          windowId,
          appStateEntityId,
          entities: matchingEntities.map((e: any) => ({
            _id: e._id,
            name: e.name,
            entityType: e.entityType
          }))
        });
        
        // 🔧 FIX: If we have appStateEntityId but didn't find it in all entities above, 
        // try to match it against the collision set (entity might have wrong llmId)
        if (appStateEntityId) {
          const matchFromAppState = matchingEntities.find((e: any) => e._id === appStateEntityId || e.id === appStateEntityId);
          if (matchFromAppState) {
            console.log('[getWindowEntity] ✅ Resolved collision using applicationState entityId:', {
              windowId,
              entityId: appStateEntityId,
              entityName: matchFromAppState.name
            });
            return matchFromAppState;
          }
        }
        
        // 🚨 CRITICAL: Collision and no way to resolve - return null to prevent wrong entity
        console.error('[getWindowEntity] ❌ Cannot resolve llmId collision - returning null to prevent wrong entity!');
        return null;
      }
      
      entity = matchingEntities[0];
      if (entity) {
        return entity;
      }
    }
    
    // Only warn for entity-backed windows (Doc, Note, etc.) — not for app-type windows
    // where entityId is optional context (e.g. selected mail thread) and may not exist yet.
    if (!window.appType) {
      console.warn('[getWindowEntity] Entity not found', {
        windowId,
        windowEntityId: window.entityId,
      });
    }
    
    return null;
  },

  getWindowWithEntity: (windowId: string) => {
    const window = get().windows[windowId];
    if (!window) return null;
    
    // ✅ REFACTOR: Use getWindowEntity helper (which now uses entityStore)
    const entity = get().getWindowEntity(windowId);
    return {
      ...window,
      entity
    } as AnyWindowEntity;
  },

  getAllWindowsWithEntities: () => {
    const state = get();
    const windowsWithEntities: Record<string, AnyWindowEntity> = {};
    
    // ✅ REFACTOR: Use getWindowEntity helper for each window
    for (const [windowId, window] of Object.entries(state.windows)) {
      const entity = get().getWindowEntity(windowId);
      windowsWithEntities[windowId] = {
        ...window,
        entity
      } as AnyWindowEntity;
    }
    
    return windowsWithEntities;
  },

  // ✅ REFACTOR: setEntity removed - entityStore is single source of truth
  // Components read entities directly from entityStore via helper methods
  // No manual sync needed between stores

  // ✅ NEW: Centralized entity update that syncs everywhere
  updateEntityEverywhere: async (entityId: string, updates: Partial<any>) => {
    try {
      // 1. Update backend first (source of truth)
      const { entityService } = await import('../services/entityService');
      
      // Add error handling for backend update
      let updatedEntity;
      try {
        updatedEntity = await entityService.update(entityId, updates);
      } catch (backendError) {
        log.error('[SpaceStore] Backend update failed:', { entityId, error: backendError });
        // If backend fails, try to continue with local updates only
        throw new Error(`Backend update failed: ${backendError instanceof Error ? backendError.message : 'Unknown error'}`);
      }

      // 2. Update EntityStore via the canonical pipeline (normalization,
      //    connectedIntegrations, etc. are applied automatically).
      useEntityStore.getState()._applyLocalUpsert(updatedEntity, {
        source: 'local',
        log: false,
        syncCRDT: false,
      });

      // Real-time sync handled by entity-system Y.Doc (broadcast from REST handler).

      // 5. Emit entity updated event to propagate across all stores
      const state = get();
      const { entityEventService } = await import('../services/entityEventService');
      entityEventService.entityUpdated(
        updatedEntity,
        state.activeSpace?.id || '',
        {
          command: 'updateEntityEverywhere'
        }
      );
      log.info('[SpaceStore] ✅ Emitted entity updated event');

      return updatedEntity;
    } catch (error) {
      log.error('[SpaceStore] updateEntityEverywhere failed:', error);
      throw error;
    }
  },

  // ✅ NEW: Fix broken entity associations for windows
  fixWindowEntityAssociation: async (windowId: string) => {
    const state = get();
    const window = state.windows[windowId];
    
    if (!window) {
      log.info(`[SpaceStore] Window ${windowId} doesn't exist`);
      return;
    }
    
    // ✅ REFACTOR: Check if window has entityLlmId and entity exists in entityStore
    if (window.entityLlmId) {
      const { default: useEntityStore } = await import('./entityStore');
      const entityStore = useEntityStore.getState();
      const entity = entityStore.entities.find((e: any) => e.llmId === window.entityLlmId);
      if (entity) {
        log.info(`[SpaceStore] Window ${windowId} already has valid entity association`);
        return;
      } else {
        log.warn(`[SpaceStore] Window ${windowId} has entityLlmId but entity is missing from entityStore - will re-fetch`);
        // Continue to re-fetch the missing entity
      }
    }
    
    // Try to find entity by document ID from window OR existing entityLlmId
    const { getWindowEntityId } = await import('../components/Space/utils/windowUtils');
    const docId = getWindowEntityId(window);
    const entityLlmId = window.entityLlmId;
    
    if (!docId && !entityLlmId) {
      log.warn(`[SpaceStore] No entityId or entityLlmId found for window ${windowId}:`, { 
        windowEntityId: window.entityId,
        applicationState: window.applicationState,
        entityLlmId: window.entityLlmId 
      });
      return;
    }
    
    log.info(formatJSON({ windowId, docId, entityLlmId }, '[SpaceStore] 🔧 Fixing entity association for window'));
    
    try {
      // Check if entity already exists in entities store
      // ✅ REFACTOR: Use entityStore as single source of truth
      const { default: useEntityStore } = await import('./entityStore');
      const entityStore = useEntityStore.getState();
      
      const existingEntity = entityStore.entities.find((entity: any) => 
        (docId && (entity._id === docId || entity.id === docId)) ||
        (entityLlmId && entity.llmId === entityLlmId)
      );
      
      if (existingEntity && existingEntity.llmId) {
        
        // Update window with entity reference
        const updatedWindow = {
          ...window,
          entityId: existingEntity._id || existingEntity.id,
          entityLlmId: existingEntity.llmId
        };
        
        set((state) => ({
          windows: {
            ...state.windows,
            [windowId]: updatedWindow
          }
        }));
        
        log.info(formatJSON({
          windowId,
          entityLlmId: existingEntity.llmId,
          entityName: existingEntity.name
        }, '[SpaceStore] ✅ Fixed entity association using entityStore'));
      } else {
        // ✅ FALLBACK: Try direct backend fetch if EntityStore is empty
        // Priority: docId (actual _id) > entityId (window reference) > entityLlmId (last resort)
        const fetchId = docId || window.entityId || entityLlmId;
        if (!fetchId) {
          log.warn(`[SpaceStore] No valid ID to fetch entity for window ${windowId}`);
          return;
        }
        
        const fetchType = docId ? 'docId' : window.entityId ? 'entityId' : 'entityLlmId';
        log.info(`[SpaceStore] Entity not found in stores, trying direct backend fetch for ${fetchType}: ${fetchId}`);
        try {
          // ✅ USE ENTITY ACTIONS: Fetch entity data using proper store
          const backendEntity = await useEntityStore.getState().actions.getEntity(fetchId);
          if (backendEntity && backendEntity.llmId) {
            // ✅ REFACTOR: No need to store in spaceStore - entityStore is single source of truth
            
            // Update window with entity reference
            const updatedWindow = {
              ...window,
              entityId: backendEntity._id || backendEntity.id,
              entityLlmId: backendEntity.llmId
            };
            
            set((state) => ({
              windows: {
                ...state.windows,
                [windowId]: updatedWindow
              }
            }));
            
            log.info(formatJSON({
              windowId,
              entityLlmId: backendEntity.llmId,
              entityName: backendEntity.name
            }, '[SpaceStore] ✅ Fixed entity association using direct backend fetch'));
          } else {
            log.warn(`[SpaceStore] ❌ Entity ${docId} not found in backend or missing llmId`);
          }
        } catch (backendError) {
          log.warn(`[SpaceStore] ❌ Could not fetch entity ${fetchId} from backend:`, backendError);
          
          // ✅ CONSERVATIVE: Don't delete windows on 404 - they might just have stale references
          // Only log the error and continue - let user manually clean up if needed
          log.warn(`[SpaceStore] ⚠️ Could not fetch entity for window ${windowId}, but keeping window (entity: ${fetchId})`);
          
          // TODO: In the future, we could mark these windows as "needs attention" in the UI
          // rather than deleting them automatically
        }
      }
    } catch (error) {
      log.error(`[SpaceStore] Error fixing entity association for window ${windowId}:`, error);
    }
  },

  // ✅ NEW: Fix all windows with missing entity associations
  fixAllWindowEntityAssociations: async () => {
    const state = get();
    const windowsToFix = Object.keys(state.windows).filter(windowId => {
      const window = state.windows[windowId];
      return window && !window.entityLlmId && (
        window.applicationState?.docs?.activeDocId ||
        window.applicationState?.pdfium?.entityId ||
        window.applicationState?.notes?.activeNoteId
      );
    });
    
    log.info(`[SpaceStore] 🔧 Fixing entity associations for ${windowsToFix.length} windows:`, windowsToFix);
    
    for (const windowId of windowsToFix) {
      await get().fixWindowEntityAssociation(windowId);
    }
    
    log.info(`[SpaceStore] ✅ Completed fixing entity associations for all windows`);
  },

  // ✅ NEW: Context management methods
  addToContext: (llmId: string, isManuallySet: boolean = false) => {
    const state = get();
    log.log('[SpaceStore] ===== ADD TO CONTEXT START =====');
    log.log('[SpaceStore] Adding entity to persistent context:', { llmId, isManuallySet });
    
    // Check if already in context
    if (state.contextEntities[llmId]) {
      log.log('[SpaceStore] ⚠️ Entity already in context, updating timestamp:', llmId);
    } else {
      log.log('[SpaceStore] ✅ Adding new entity to context:', llmId);
    }
    
    set((state) => ({
      contextEntities: {
        ...state.contextEntities,
        [llmId]: {
          llmId,
          isManuallySet,
          addedAt: Date.now()
        }
      }
    }));
    
    log.log('[SpaceStore] ✅ Successfully added to contextEntities');
    log.log('[SpaceStore] Current context entities count:', Object.keys(get().contextEntities).length);
    log.log('[SpaceStore] ===== ADD TO CONTEXT END =====');
    
    // Schedule backend save to persist context state
    backendSaveQueue.schedule(500);
  },

  removeFromContext: (llmId: string) => {
    const state = get();
    log.info('[SpaceStore] Removing entity from persistent context:', { llmId });
    
    set((state) => {
      const { [llmId]: removed, ...remainingContext } = state.contextEntities;
      return { contextEntities: remainingContext };
    });
    
    // Schedule backend save to persist context state
    backendSaveQueue.schedule(500);
  },

  getContextEntities: () => {
    return get().contextEntities;
  },

  syncContextWithContextStore: async () => {
    const state = get();
    
    try {
      // Import contextStore dynamically to avoid circular dependencies
      const { useContextStore } = await import('../components/Space/SpaceChat/store/contextStore');
      const contextStore = useContextStore.getState();
      
      // Get current context from contextStore
      const currentContext = contextStore.context;
      const newContextEntities: Record<string, { llmId: string; isManuallySet: boolean; addedAt: number }> = {};
      
      // Sync from contextStore entityRefs (keys are llmIds now)
      Object.entries(((currentContext as any).entityRefs) || {}).forEach(([llmId, content]) => {
        if (llmId && (content as any).entityLlmId) {
          newContextEntities[llmId] = {
            llmId: llmId,
            isManuallySet: (content as any).isManuallySet || false,
            addedAt: Date.now()
          };
        }
      });
      
      set({ contextEntities: newContextEntities });
      
      // Schedule backend save
      backendSaveQueue.schedule(500);
    } catch (error) {
      log.error('[SpaceStore] Error syncing context with contextStore:', error);
    }
  },

  // Excerpt persistence methods
  addExcerpt: (excerpt: any) => {
    set((state) => ({ contextExcerpts: [...(state.contextExcerpts || []), excerpt] }));
    backendSaveQueue.schedule(500);
  },
  removeExcerpt: (index: number) => {
    set((state) => ({ contextExcerpts: (state.contextExcerpts || []).filter((_, i) => i !== index) }));
    backendSaveQueue.schedule(500);
  },
  clearExcerpts: () => {
    set({ contextExcerpts: [] });
    backendSaveQueue.schedule(500);
  },
  
  removeContextEntity: (llmId: string) => {
    log.info(`[SpaceStore] Removing context entity: ${llmId}`);
    set((state) => {
      const { [llmId]: removed, ...remainingContextEntities } = state.contextEntities;
      return { contextEntities: remainingContextEntities };
    });
    backendSaveQueue.schedule(500);
  },

  // ✅ NEW: Clean up stale window references
  cleanupStaleWindows: async () => {
    const state = get();
    const spaceId = state.activeSpace?.id;
    if (!spaceId) return;

    log.info('[SpaceStore] 🧹 Cleaning up stale window references');
    
    const validWindows: Record<string, NormalizedWindowEntity> = {};
    const staleWindowIds: string[] = [];
    const orphanedChildWindowIds: string[] = [];
    
    // First pass: Check each window to see if its entity actually exists
    for (const [windowId, window] of Object.entries(state.windows)) {
      const docId = window.applicationState?.docs?.activeDocId || 
                   window.applicationState?.pdfium?.entityId ||
                   window.applicationState?.notes?.activeNoteId;
      
      if (docId) {
        try {
          // ✅ USE ENTITY ACTIONS: Fetch entity data using proper store
          await useEntityStore.getState().actions.getEntity(docId);
          validWindows[windowId] = window;
          log.info(`[SpaceStore] ✅ Window ${windowId} has valid entity ${docId}`);
        } catch (error) {
          // Entity doesn't exist or can't be fetched
          staleWindowIds.push(windowId);
          log.warn(`[SpaceStore] ❌ Window ${windowId} has stale entity reference ${docId}:`, error);
        }
      } else {
        // Window without entity reference - keep it
        validWindows[windowId] = window;
      }
    }
    
    // Second pass: Check for orphaned child windows (parent doesn't exist)
    for (const [windowId, window] of Object.entries(state.windows)) {
      if (window.isChildWindow && window.parentWindowId) {
        const parentExists = state.windows[window.parentWindowId] !== undefined;
        if (!parentExists) {
          orphanedChildWindowIds.push(windowId);
          log.warn(`[SpaceStore] ❌ Window ${windowId} is orphaned (parent ${window.parentWindowId} doesn't exist)`);
        }
      }
    }
    
    // Combine all windows to remove
    const allWindowsToRemove = [...new Set([...staleWindowIds, ...orphanedChildWindowIds])];
    
    if (allWindowsToRemove.length > 0) {
      log.info(`[SpaceStore] 🗑️ Removing ${allWindowsToRemove.length} windows:`, {
        stale: staleWindowIds,
        orphaned: orphanedChildWindowIds,
        total: allWindowsToRemove
      });
      
      // ✅ FIX: Use helper instead of direct state mutation
      allWindowsToRemove.forEach(windowId => {
        get()._applyLocalWindowDelete(windowId, {
          source: 'local',
          syncCRDT: true,
          log: true
        });
      });
      
      // Force immediate backend save
      await backendSaveQueue.flush();
      log.info(`[SpaceStore] ✅ Cleanup complete - removed ${allWindowsToRemove.length} windows (${staleWindowIds.length} stale, ${orphanedChildWindowIds.length} orphaned)`);
    } else {
      log.info('[SpaceStore] ✅ No stale or orphaned windows found');
    }
  },

  // ✅ NEW: Force sync CRDT with current local state (nuclear option)
  forceSyncCRDTWithLocalState: async () => {
    const state = get();
    const spaceId = state.activeSpace?.id;
    if (!spaceId) return;

    log.info('[SpaceStore] 🚀 Force syncing CRDT with local state');
    
    // Get current local windows
    const localWindows = state.windows;
    log.info(`[SpaceStore] Local state has ${Object.keys(localWindows).length} windows`);
    
    // Force update CRDT to match local state exactly
    for (const [windowId, window] of Object.entries(localWindows)) {
      spaceCRDTService.updateWindow(spaceId, windowId, window);
    }
    
    // Force backend save
    await backendSaveQueue.flush();
    
    log.info(`[SpaceStore] ✅ Force sync complete - CRDT now matches local state`);
  },
  
  // Expose internal helpers
  _applyLocalWindowUpdate,
  _applyLocalWindowDelete,
  
  // ✅ PERFORMANCE: Lazy window loading methods
  markWindowAsLoaded: (windowId: string) => {
    const currentSize = get().loadedWindowIds.size;
    const wasAlreadyLoaded = get().loadedWindowIds.has(windowId);
    
    set((state) => {
      const newSet = new Set(state.loadedWindowIds);
      newSet.add(windowId);
      return { loadedWindowIds: newSet };
    });
    
    const newSize = get().loadedWindowIds.size;
    
    if (!wasAlreadyLoaded) {
      const total = Object.keys(get().windows).length;
      console.log(`🚀 [LAZY-LOAD] Window marked for loading: ${windowId.slice(-12)} loaded=${newSize}/${total} (${Math.round((newSize / total) * 100)}%)`);
    }
  },
  
  getLoadedWindowIds: () => {
    return get().loadedWindowIds;
  },
  
  // Mount actions
  actions: createSpaceActions(set, get),
}});

export type { AnyWindowEntity, WindowEntity };
export default useSpaceStore;

const createUISpace = (id: string): Space => {
	return {
		id: id,
		name: 'New Space',
		windows: {},
		windowOrder: [],
		contextEntities: {}, // ✅ NEW: Initialize empty context state
		activeWindowId: null,
		settings: {
			isContentsVisible: false,
			isChatVisible: false, // Default to hidden for new spaces
			viewMode: 'focused' as const,
			background: { ...DEFAULT_NEW_SPACE_BACKGROUND },
			sidebar: {
				isVisible: false, // Default to collapsed - only open on hover
				width: 250, // ✅ UPDATED: Keep 250px as requested
				mode: 'normal',
			},
			spaceChat: {
				currentThreadIds: {},
				isHistoryOpen: false,
				mode: 'chat',
				model: modelService.getPlanAwareDefaultModel(),
				isContextVisible: false,
				widths: {
					focused: 480, // Default chat width for focused layout (wider for better UX)
					spatial: 320  // Default chat width for spatial layout
				}
			}
		},
		createdAt: new Date().toISOString(),
		updatedAt: new Date().toISOString(),
	}
}

// ✅ REMOVED: Duplicate command registration function
// Commands are now registered centrally via commandRegistryStore.initializeAllCommands()
// This eliminates duplicate registrations and ensures single source of truth

